import React from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Grid,
  Input,
  Modal,
  Progress,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  InboxOutlined,
  ShopOutlined,
  SearchOutlined,
  RightOutlined,
  StopOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";
import dayjs from "dayjs";
import { useGetRetailDashboardQuery } from "../../../Redux/service/NewReports/ShelfAvailability";
import trans, { isRTL } from "../../../utils";
import {
  arrayOrEmpty,
  numberOrZero,
  optionalNumber,
  displayText,
  metricCount,
  metricRate,
  stockCategoryKey,
  filterStockRowsBySelection,
  stockItemKey,
  getStockItemOptions,
  getStockCategoryOptions,
  filterRowsByStockSelection,
  buildItemStockRows,
  deriveRetailView,
} from "./retail/retailMetrics";
import {
  NEUTRAL_BAND_PP,
  rateDeltaPp,
  countDelta,
  isLowSample,
  indexByKey,
  alignTrendByDayOfMonth,
  sumTrendItems,
  createVisitLookup,
  unionOptions,
  rankMovers,
} from "./retail/retailCompare";
import DeltaBadge from "./widgets/DeltaBadge";
import RetailComparisonSummary from "./retail/RetailComparisonSummary";

const { Text, Title } = Typography;

const COLORS = {
  primary: "#0f6936",
  success: "#16a34a",
  danger: "#dc2626",
  warning: "#ea580c",
  info: "#0284c7",
  neutral: "#64748b",
  dark: "#1e293b",
  border: "#e2e8f0",
  surface: "#ffffff",
  background: "#f8fafc",
};

const formatNumber = (value, maximumFractionDigits = 0) =>
  numberOrZero(value).toLocaleString(undefined, { maximumFractionDigits });

const formatDate = (value, format = "DD MMM YYYY") => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : "—";
};

// Dashboard buckets are calendar dates, not instants. Parse the YYYY-MM-DD
// portion locally so UTC offsets cannot move a bucket or selected period to an
// adjacent day; submission timestamps continue to use formatDate above.
const formatCalendarDate = (value, format = "DD MMM YYYY") => {
  if (!value) return "—";
  const dateValue =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)
      ? value.slice(0, 10)
      : value;
  const parsed = dayjs(dateValue);
  return parsed.isValid() ? parsed.format(format) : "—";
};

const locationText = (...values) => {
  const labels = values
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(displayText)
    .filter((value) => value !== "—");
  return labels.join(" / ") || "—";
};

const formatRate = (value) => {
  const parsed = optionalNumber(value);
  if (parsed === null) return "\u2014";
  if (parsed > 0 && parsed < 0.1) return "<0.1%";
  return `${formatNumber(parsed, 1)}%`;
};

const metricTooltip = (row, metric) => {
  const count = metricCount(row, metric);
  const total = optionalNumber(row?.totalChecks);
  const label =
    metric === "oos"
      ? trans("Out-of-stock checks")
      : metric === "available"
        ? trans("Shelf-available checks")
        : trans("Shelf-gap checks");

  if (count === null) return trans("Exact count is not available");
  return total !== null
    ? `${label}: ${formatNumber(count)} / ${formatNumber(total)}`
    : `${label}: ${formatNumber(count)}`;
};

const formatChannel = (value) => {
  const label = displayText(value);
  const normalized = label.toLowerCase().replace(/[\s_-]/g, "");
  if (normalized === "supermarket") return trans("Supermarket");
  if (
    normalized === "shoppingarea" ||
    normalized === "shopingarea" ||
    normalized === "shoppingcenter"
  ) {
    return trans("Shopping area");
  }
  if (normalized === "unknown") return trans("Unknown");
  return label;
};

function PercentMetric({ row, metric, color }) {
  const rate = metricRate(row, metric);

  if (rate === null) {
    return (
      <Tooltip
        title={metricTooltip(row, metric)}
        trigger={["hover", "focus"]}
      >
        <Text
          type="secondary"
          tabIndex={0}
          aria-label={metricTooltip(row, metric)}
          style={{ cursor: "help" }}
        >
          —
        </Text>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={metricTooltip(row, metric)} trigger={["hover", "focus"]}>
      <span
        tabIndex={0}
        aria-label={`${formatRate(rate)}. ${metricTooltip(row, metric)}`}
        style={{
          width: 150,
          display: "inline-block",
          cursor: "help",
          direction: "ltr",
        }}
      >
        <Progress
          percent={Number(rate.toFixed(1))}
          size="small"
          strokeColor={color}
          trailColor="#edf2f7"
          format={() => formatRate(rate)}
        />
      </span>
    </Tooltip>
  );
}

const panelStyle = {
  height: "100%",
  borderRadius: 14,
  border: `1px solid ${COLORS.border}`,
  boxShadow: "0 6px 24px rgba(15, 23, 42, 0.05)",
};

function PanelTitle({ icon, title, subtitle }) {
  return (
    <Space align="start" size={10}>
      <span
        style={{
          width: 34,
          height: 34,
          flex: "0 0 34px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 9,
          color: COLORS.primary,
          background: "#e8f5ed",
        }}
      >
        {icon}
      </span>
      <span>
        <Text strong style={{ display: "block", color: COLORS.dark }}>
          {trans(title)}
        </Text>
        {subtitle ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {trans(subtitle)}
          </Text>
        ) : null}
      </span>
    </Space>
  );
}

function EmptyPanel({ description }) {
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={trans(description || "No data available for this period")}
      style={{ marginBlock: 32 }}
    />
  );
}

const STOCK_METRICS = [
  {
    key: "available",
    label: "Shelf available",
    shortLabel: "Shelf",
    color: COLORS.success,
  },
  {
    key: "gap",
    label: "Shelf gap",
    shortLabel: "Shelf gap",
    color: COLORS.warning,
  },
  {
    key: "oos",
    label: "True out of stock",
    shortLabel: "OOS",
    color: COLORS.danger,
  },
];

const ALL_STOCK_CATEGORIES = "__all_stock_categories__";
const ALL_STOCK_ITEMS = "__all_stock_items__";
const ALL_LOCATION_REGIONS = "__all_location_regions__";
const ALL_LOCATION_CITIES = "__all_location_cities__";
const LOCATION_CLIENT_MODE = {
  all: "all",
  include: "include",
  exclude: "exclude",
};

const groupStockItemOptions = (options) => {
  const groups = new Map();
  arrayOrEmpty(options).forEach((option) => {
    const group = groups.get(option.categoryKey) || {
      label: option.categoryLabel,
      options: [],
    };
    group.options.push({ value: option.value, label: option.label });
    groups.set(option.categoryKey, group);
  });

  return [...groups.values()];
};

const nextAllAwareSelection = (nextValues, allValue, previouslyAll) => {
  const values = arrayOrEmpty(nextValues);
  if (!values.length) return [allValue];
  if (values.includes(allValue) && !previouslyAll) return [allValue];

  const specificValues = values.filter((value) => value !== allValue);
  return specificValues.length ? specificValues : [allValue];
};

const pruneAllAwareSelection = (selection, allValue, allowedValues) => {
  const values = arrayOrEmpty(selection);
  if (values.includes(allValue)) return [allValue];

  const allowed = allowedValues instanceof Set
    ? allowedValues
    : new Set(arrayOrEmpty(allowedValues));
  const validValues = values.filter((value) => allowed.has(value));
  return validValues.length ? validValues : [allValue];
};

const pruneSelection = (selection, allowedValues) => {
  const allowed =
    allowedValues instanceof Set
      ? allowedValues
      : new Set(arrayOrEmpty(allowedValues));
  return arrayOrEmpty(selection).filter((value) => allowed.has(value));
};

const sameSelection = (left, right) => {
  const normalize = (values) =>
    arrayOrEmpty(values)
      .map(String)
      .sort((a, b) => a.localeCompare(b));
  const normalizedLeft = normalize(left);
  const normalizedRight = normalize(right);
  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((value, index) => value === normalizedRight[index])
  );
};

const groupLocationOptions = (options, groupKey, groupLabel) => {
  const groups = new Map();
  arrayOrEmpty(options).forEach((option) => {
    const key = option?.[groupKey] || "unknown";
    const group = groups.get(key) || {
      label: option?.[groupLabel] || trans("Unknown"),
      options: [],
    };
    group.options.push({ value: option.value, label: option.label });
    groups.set(key, group);
  });
  return [...groups.values()];
};

const dimensionLabel = (row, dimension) => {
  if (dimension === "channel") {
    return formatChannel(row?.channelLabel || row?.channel);
  }
  if (dimension === "city") return locationText(row?.region, row?.city);
  if (dimension === "item") return displayText(row?.sku || row?.formProductKey);
  return displayText(row?.[dimension]);
};

const dimensionKey = (row, dimension) => {
  if (dimension === "category") {
    return `category:${stockCategoryKey(row?.category)}`;
  }
  if (dimension === "region") return `region:${displayText(row?.region)}`;
  if (dimension === "city") {
    return `city:${JSON.stringify([row?.region ?? null, row?.city ?? null])}`;
  }
  if (dimension === "channel") {
    return `channel:${row?.channel ?? row?.channelLabel ?? "unknown"}`;
  }
  if (dimension === "item") return `item:${stockItemKey(row)}`;
  return `${dimension}:${dimensionLabel(row, dimension)}`;
};

const detailLabel = (row, type) => {
  if (type === "items") return displayText(row?.sku || row?.item);
  if (type === "categories") return displayText(row?.category);
  if (type === "cities") return locationText(row?.region, row?.city);
  if (type === "regions") return displayText(row?.region);
  if (type === "channels") return formatChannel(row?.channelLabel || row?.channel);
  return displayText(row?.label || row?.name);
};

function StockRateText({ row, metric, fontSize = 13 }) {
  const rate = metricRate(row, metric);
  const tooltip = metricTooltip(row, metric);

  return (
    <Tooltip title={tooltip} trigger={["hover", "focus"]}>
      <Text
        strong
        tabIndex={0}
        aria-label={`${formatRate(rate)}. ${tooltip}`}
        style={{ color: COLORS.dark, fontSize, cursor: "help" }}
      >
        {formatRate(rate)}
      </Text>
    </Tooltip>
  );
}

function StockMixBar({ row, height = 18 }) {
  const metricsWithValues = STOCK_METRICS.map((metric) => ({
    ...metric,
    rate: metricRate(row, metric.key) ?? 0,
    count: metricCount(row, metric.key),
  }));
  const hasCompleteCounts = metricsWithValues.every(
    (metric) => metric.count !== null,
  );
  const totalWeight = metricsWithValues.reduce(
    (total, metric) =>
      total + (hasCompleteCounts ? metric.count : metric.rate),
    0,
  );
  const metrics = metricsWithValues.map((metric) => ({
    ...metric,
    share:
      totalWeight > 0
        ? ((hasCompleteCounts ? metric.count : metric.rate) / totalWeight) * 100
        : 0,
  }));
  const ariaLabel = metrics
    .map(
      (metric) =>
        `${trans(metric.label)} ${formatRate(metric.rate)}. ${metricTooltip(
          row,
          metric.key,
        )}`,
    )
    .join(", ");
  const tooltipContent = (
    <Space direction="vertical" size={2}>
      {metrics.map((metric) => (
        <span key={metric.key}>
          {trans(metric.label)}: {formatRate(metric.rate)} ·{" "}
          {metricTooltip(row, metric.key)}
        </span>
      ))}
    </Space>
  );

  return (
    <Tooltip title={tooltipContent} trigger={["hover", "focus"]}>
      <div
        role="img"
        tabIndex={0}
        aria-label={ariaLabel}
        style={{
          height,
          width: "100%",
          display: "flex",
          overflow: "hidden",
          borderRadius: 999,
          background: "#eef2f6",
          boxShadow: "inset 0 0 0 1px rgba(100,116,139,0.10)",
          direction: "ltr",
          cursor: "help",
        }}
      >
        {metrics.map((metric) =>
          metric.share > 0 ? (
            <span
              key={metric.key}
              aria-hidden="true"
              style={{
                width: `${metric.share}%`,
                height: "100%",
                display: "block",
                background: metric.color,
              }}
            />
          ) : null,
        )}
      </div>
    </Tooltip>
  );
}

function StockHealthDetailTable({ rows, type }) {
  const detailRows = [...arrayOrEmpty(rows)].sort(
    (a, b) => (metricRate(b, "oos") ?? -1) - (metricRate(a, "oos") ?? -1),
  );

  return (
    <Table
      size="small"
      rowKey={(row, index) => `${detailLabel(row, type)}-${index}`}
      dataSource={detailRows}
      pagination={{
        defaultPageSize: 8,
        size: "small",
        hideOnSinglePage: false,
        showSizeChanger: detailRows.length > 8,
        pageSizeOptions: ["8", "16", "32"],
        position: ["bottomCenter"],
        showTotal: (total, range) =>
          `${formatNumber(range[0])}–${formatNumber(range[1])} / ${formatNumber(
            total,
          )}`,
      }}
      scroll={{
        x: 760,
        y: "clamp(80px, calc(100dvh - 540px), 360px)",
        scrollToFirstRowOnChange: true,
      }}
      columns={[
        {
          title: trans(
            {
              items: "Item",
              categories: "Category",
              cities: "City",
              regions: "Region",
              channels: "Channel",
            }[type] || "Details",
          ),
          key: "label",
          width: 190,
          render: (_, row) => (
            <Text strong style={{ color: COLORS.dark }}>
              {detailLabel(row, type)}
            </Text>
          ),
        },
        {
          title: trans("Stock mix"),
          key: "stockMix",
          width: 250,
          render: (_, row) => <StockMixBar row={row} height={14} />,
        },
        {
          title: trans("Shelf available"),
          key: "available",
          align: "right",
          render: (_, row) => <StockRateText row={row} metric="available" />,
        },
        {
          title: trans("Shelf gap"),
          key: "gap",
          align: "right",
          render: (_, row) => <StockRateText row={row} metric="gap" />,
        },
        {
          title: trans("True out of stock"),
          key: "oos",
          align: "right",
          render: (_, row) => <StockRateText row={row} metric="oos" />,
        },
      ]}
    />
  );
}

const detailSectionsFor = (row, dimension) => {
  if (!row) return [];
  const breakdown = row?.breakdown || {};
  const candidates = {
    items: breakdown?.items || breakdown?.products || row?.items,
    categories: breakdown?.categories,
    cities: breakdown?.cities,
    regions: breakdown?.regions,
    channels: breakdown?.channels,
  };
  const order = {
    item: ["regions", "cities", "channels"],
    category: ["items"],
    region: ["cities", "categories", "items"],
    city: ["categories", "items"],
    channel: ["regions", "categories", "items"],
  }[dimension] || ["items"];

  return order
    .map((type) => ({ type, rows: arrayOrEmpty(candidates[type]) }))
    .filter((section) => section.rows.length > 0);
};

function StockRateBadge({ row, metric }) {
  const config = STOCK_METRICS.find((item) => item.key === metric);
  const rate = metricRate(row, metric);
  const tooltip = metricTooltip(row, metric);

  return (
    <Tooltip
      title={`${trans(config.label)}: ${formatRate(rate)} · ${tooltip}`}
      trigger={["hover", "focus"]}
    >
      <Tag
        aria-label={`${trans(config.label)} ${formatRate(rate)}. ${tooltip}`}
        style={{
          minWidth: 66,
          marginInlineEnd: 0,
          paddingInline: 7,
          textAlign: "center",
          color: COLORS.dark,
          background: `${config.color}12`,
          borderColor: `${config.color}45`,
          fontWeight: 700,
          cursor: "help",
          direction: "ltr",
        }}
      >
        <span style={{ fontWeight: 500 }}>{trans(config.shortLabel)}</span>{" "}
        {formatRate(rate)}
      </Tag>
    </Tooltip>
  );
}

// Month names for comparison badges: `from` is the comparison month, `to`
// the selected month.
const ComparisonLabelsContext = React.createContext({ from: "", to: "" });

// Shelf-availability change of one stock-health row versus the comparison
// month's matching row (snapshot rates, compared in percentage points).
// Higher availability is better, so an increase shows green.
function StockAvailabilityDelta({ row, previous, loading }) {
  const labels = React.useContext(ComparisonLabelsContext);
  if (loading) return <DeltaBadge status="loading" />;
  const hasPrevious = numberOrZero(previous?.totalChecks) > 0;
  const previousRate = hasPrevious ? metricRate(previous, "available") : null;
  const currentRate = metricRate(row, "available");
  return (
    <DeltaBadge
      delta={rateDeltaPp(currentRate, previousRate)}
      status={hasPrevious ? undefined : "new"}
      positiveIsGood
      neutralBand={NEUTRAL_BAND_PP}
      lowSample={
        hasPrevious && isLowSample(row?.totalChecks, previous?.totalChecks)
      }
      from={hasPrevious ? formatRate(previousRate) : trans("No stock checks")}
      to={formatRate(currentRate)}
      fromLabel={labels.from}
      toLabel={labels.to}
      note={trans("Shelf availability rate")}
    />
  );
}

function DistributionPanel({
  data,
  dimension,
  selectedCategoryKeys = [],
  selectedItemKeys = [],
  comparisonData = null,
  comparisonLabel = "",
  comparisonLoading = false,
}) {
  const listRef = React.useRef(null);
  const [search, setSearch] = React.useState("");
  // Items are listed in full by default; other dimensions start at Top 10.
  const [viewMode, setViewMode] = React.useState(
    dimension === "item" ? "all" : "top",
  );
  const [sortMode, setSortMode] = React.useState("oos");
  const [selectedKey, setSelectedKey] = React.useState(null);
  const stockFilterSignature = [
    ...selectedCategoryKeys,
    "::items::",
    ...selectedItemKeys,
  ].join("|");
  const stockFilterActive =
    selectedCategoryKeys.length > 0 || selectedItemKeys.length > 0;
  // Item rows are themselves items, so the stock selection filters them
  // directly; other dimensions re-aggregate their per-item breakdowns.
  const selectRows = (source) =>
    dimension === "item"
      ? filterRowsByStockSelection(source, selectedCategoryKeys, selectedItemKeys)
      : filterStockRowsBySelection(
          source,
          dimension,
          selectedCategoryKeys,
          selectedItemKeys,
        );
  const rows = selectRows(data);
  // The comparison month goes through the same stock filter, then rows are
  // matched on the same dimension key.
  const comparisonIndex = comparisonData
    ? indexByKey(selectRows(comparisonData), (row) =>
        dimensionKey(row, dimension),
      )
    : null;
  const showComparison = Boolean(comparisonIndex) || comparisonLoading;
  const selectedRow =
    rows.find((row) => dimensionKey(row, dimension) === selectedKey) || null;
  React.useEffect(() => {
    if (selectedKey && data && !selectedRow) setSelectedKey(null);
  }, [data, selectedKey, selectedRow]);
  React.useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [search, sortMode, viewMode, stockFilterSignature]);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const dimensionTitle = {
    category: "Category",
    region: "Region",
    city: "City",
    channel: "Channel",
    item: "Item",
  }[dimension];

  const filteredRows = rows.filter((row) =>
    [
      dimensionLabel(row, dimension),
      displayText(row?.category),
      displayText(row?.region),
      displayText(row?.city),
      formatChannel(row?.channelLabel || row?.channel),
    ]
      .join(" ")
      .toLocaleLowerCase()
      .includes(normalizedSearch),
  );
  const compareNames = (a, b) =>
    dimensionLabel(a, dimension).localeCompare(
      dimensionLabel(b, dimension),
      undefined,
      { numeric: true, sensitivity: "base" },
    );
  const sortedRows = [...filteredRows].sort((a, b) => {
    if (sortMode === "gap") {
      return (
        (metricRate(b, "gap") ?? -1) - (metricRate(a, "gap") ?? -1) ||
        (metricCount(b, "gap") ?? -1) - (metricCount(a, "gap") ?? -1) ||
        numberOrZero(b?.totalChecks) - numberOrZero(a?.totalChecks) ||
        compareNames(a, b)
      );
    }
    if (sortMode === "highAvailability") {
      return (
        (metricRate(b, "available") ?? -1) -
          (metricRate(a, "available") ?? -1) ||
        (metricCount(b, "available") ?? -1) -
          (metricCount(a, "available") ?? -1) ||
        numberOrZero(b?.totalChecks) - numberOrZero(a?.totalChecks) ||
        compareNames(a, b)
      );
    }
    if (sortMode === "name") {
      return compareNames(a, b);
    }
    return (
      (metricRate(b, "oos") ?? -1) - (metricRate(a, "oos") ?? -1) ||
      (metricCount(b, "oos") ?? -1) - (metricCount(a, "oos") ?? -1) ||
      numberOrZero(b?.totalChecks) - numberOrZero(a?.totalChecks) ||
      compareNames(a, b)
    );
  });
  const visibleRows = viewMode === "top" ? sortedRows.slice(0, 10) : sortedRows;
  const detailSections = detailSectionsFor(selectedRow, dimension);
  const openDetailsButton = (row) => (
    <Button
      type="link"
      size="small"
      aria-label={`${trans("View Details")}: ${dimensionLabel(
        row,
        dimension,
      )}`}
      onClick={() => setSelectedKey(dimensionKey(row, dimension))}
      style={{ color: COLORS.primary, paddingInline: 4 }}
    >
      {trans("Details")}
      <RightOutlined
        style={{
          color: COLORS.primary,
          marginInlineStart: 5,
          transform: isRTL ? "rotate(180deg)" : undefined,
        }}
      />
    </Button>
  );

  if (!rows.length) {
    return (
      <EmptyPanel
        description={
          stockFilterActive
            ? "No stock checks for selected filters"
            : undefined
        }
      />
    );
  }

  return (
    <>
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Input
            allowClear
            value={search}
            prefix={<SearchOutlined style={{ color: COLORS.neutral }} />}
            placeholder={trans("Search this dimension")}
            aria-label={trans("Search this dimension")}
            onChange={(event) => setSearch(event.target.value)}
            style={{ flex: "1 1 180px", minWidth: 0 }}
          />
          <Select
            value={sortMode}
            onChange={setSortMode}
            aria-label={trans("Sort stock health")}
            style={{ flex: "1 1 190px", minWidth: 0 }}
            options={[
              { value: "oos", label: trans("Highest OOS") },
              {
                value: "gap",
                label: trans("Highest shelf gaps"),
              },
              {
                value: "highAvailability",
                label: trans("Highest shelf availability"),
              },
              { value: "name", label: trans("Name A-Z") },
            ]}
          />
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              { value: "top", label: trans("Top 10") },
              { value: "all", label: trans("All") },
            ]}
          />
        </div>

        <Space wrap size={[18, 6]}>
          {STOCK_METRICS.map((metric) => (
            <Space key={metric.key} size={6}>
              <span
                aria-hidden="true"
                style={{
                  width: 9,
                  height: 9,
                  display: "inline-block",
                  borderRadius: 99,
                  background: metric.color,
                }}
              />
              <Text style={{ color: COLORS.dark, fontSize: 12 }}>
                {trans(metric.label)}
              </Text>
            </Space>
          ))}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatNumber(visibleRows.length)} / {formatNumber(filteredRows.length)}{" "}
            {trans("results")}
          </Text>
        </Space>

        {visibleRows.length ? (
          <div
            ref={listRef}
            role="list"
            aria-label={`${trans("Stock health by dimension")}: ${trans(
              dimensionTitle,
            )}`}
            style={{
              maxHeight: 480,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              overflowY: "auto",
              paddingInlineEnd: 4,
              scrollbarGutter: "stable",
            }}
          >
            {visibleRows.map((row, index) => {
              const hasStockChecks = numberOrZero(row?.totalChecks) > 0;
              return (
                <div
                  role="listitem"
                  key={dimensionKey(row, dimension) || index}
                  style={{
                    padding: "9px 10px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                    background: COLORS.surface,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        minWidth: 0,
                        flex: "1 1 140px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Tooltip title={dimensionLabel(row, dimension)}>
                        <Text strong ellipsis style={{ color: COLORS.dark }}>
                          {dimensionLabel(row, dimension)}
                        </Text>
                      </Tooltip>
                      {dimension === "item" && row?.category ? (
                        <Text type="secondary" ellipsis style={{ fontSize: 11 }}>
                          {displayText(row.category)}
                        </Text>
                      ) : null}
                    </span>
                    {hasStockChecks ? (
                      <Space size={4} wrap>
                        {STOCK_METRICS.map((metric) => (
                          <StockRateBadge
                            key={metric.key}
                            row={row}
                            metric={metric.key}
                          />
                        ))}
                      </Space>
                    ) : (
                      <Tooltip
                        title={trans(
                          "Completed visits without a submitted stock report",
                        )}
                      >
                        <Tag
                          icon={<WarningOutlined />}
                          style={{
                            marginInlineEnd: 0,
                            color: COLORS.dark,
                            background: COLORS.background,
                            borderColor: COLORS.border,
                          }}
                        >
                          {trans("No stock report")} ·{" "}
                          {formatNumber(row?.completedVisits)} {trans("visits")}
                        </Tag>
                      </Tooltip>
                    )}
                    {hasStockChecks && showComparison ? (
                      <StockAvailabilityDelta
                        row={row}
                        previous={comparisonIndex?.get(
                          dimensionKey(row, dimension),
                        )}
                        loading={comparisonLoading}
                      />
                    ) : null}
                    {hasStockChecks ? openDetailsButton(row) : null}
                  </div>
                  {hasStockChecks ? (
                    <div style={{ marginTop: 8 }}>
                      <StockMixBar row={row} height={9} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyPanel description="No matching stock-health results" />
        )}
      </Space>

      <Modal
        open={Boolean(selectedRow)}
        onCancel={() => setSelectedKey(null)}
        width={880}
        centered
        destroyOnClose
        keyboard
        maskClosable
        focusTriggerAfterClose
        title={
          <span>
            <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              {trans("Stock health details")}
              {selectedCategoryKeys.length && dimension !== "category"
                ? ` · ${formatNumber(selectedCategoryKeys.length)} ${trans(
                    selectedCategoryKeys.length === 1
                      ? "Category"
                      : "Categories",
                  )}`
                : ""}
              {selectedItemKeys.length
                ? ` · ${formatNumber(selectedItemKeys.length)} ${trans("Items")}`
                : ""}
            </Text>
            <Text strong style={{ color: COLORS.dark }}>
              {selectedRow ? dimensionLabel(selectedRow, dimension) : ""}
            </Text>
          </span>
        }
        footer={
          <Button onClick={() => setSelectedKey(null)}>
            {trans("Close")}
          </Button>
        }
        styles={{
          content: {
            maxHeight: "calc(100dvh - 24px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          header: { flex: "0 0 auto" },
          body: {
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
            background: COLORS.background,
          },
          footer: { flex: "0 0 auto" },
        }}
      >
        {selectedRow ? (
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 12,
              }}
            >
              {STOCK_METRICS.map((metric) => (
                <Card
                  key={metric.key}
                  size="small"
                  style={{ borderRadius: 10, borderColor: COLORS.border }}
                >
                  <Space direction="vertical" size={5} style={{ width: "100%" }}>
                    <Space size={7}>
                      <span
                        aria-hidden="true"
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: 99,
                          background: metric.color,
                        }}
                      />
                      <Text style={{ color: COLORS.dark, fontSize: 12 }}>
                        {trans(metric.label)}
                      </Text>
                    </Space>
                    <StockRateText
                      row={selectedRow}
                      metric={metric.key}
                      fontSize={24}
                    />
                  </Space>
                </Card>
              ))}
            </div>
            <StockMixBar row={selectedRow} height={22} />
            {detailSections.length ? (
              <Tabs
                items={detailSections.map((section) => ({
                  key: section.type,
                  label: trans(
                    {
                      items: "Items",
                      categories: "Categories",
                      cities: "Cities",
                      regions: "Regions",
                      channels: "Channels",
                    }[section.type],
                  ),
                  children: (
                    <StockHealthDetailTable
                      rows={section.rows}
                      type={section.type}
                    />
                  ),
                }))}
              />
            ) : (
              <EmptyPanel description="No detailed stock-health breakdown available" />
            )}
          </Space>
        ) : null}
      </Modal>
    </>
  );
}

function VisitsTable({
  data,
  previousVisits = null,
  comparisonLabel = "",
  comparisonLoading = false,
}) {
  const rows = arrayOrEmpty(data);
  if (!rows.length)
    return <EmptyPanel description="No client visits in this period" />;

  const showComparison = Boolean(previousVisits) || comparisonLoading;
  const comparisonColumns = showComparison
    ? [
        {
          title: `${trans("vs")} ${comparisonLabel}`,
          key: "visitsChange",
          align: "right",
          render: (_, row) => {
            if (comparisonLoading) return <DeltaBadge status="loading" />;
            const previous = previousVisits(row?.clientId);
            return (
              <DeltaBadge
                unit="count"
                delta={
                  previous === null ? null : countDelta(row?.visits, previous)
                }
                from={previous === null ? trans("Not available") : formatNumber(previous)}
                to={formatNumber(row?.visits)}
                fromLabel={comparisonLabel}
                note={trans("Visits")}
              />
            );
          },
        },
      ]
    : [];

  return (
    <Table
      size="small"
      rowKey={(row, index) =>
        `${row?.clientId || row?.clientName || "client"}-${index}`
      }
      dataSource={rows}
      pagination={false}
      scroll={{ x: showComparison ? 760 : 650 }}
      columns={[
        {
          title: trans("Client"),
          dataIndex: "clientName",
          key: "clientName",
          render: (value, row) => (
            <Text strong>{displayText(value || row?.clientId)}</Text>
          ),
        },
        {
          title: trans("Region / City"),
          key: "location",
          render: (_, row) => locationText(row?.region, row?.city),
        },
        {
          title: trans("Visits"),
          dataIndex: "visits",
          key: "visits",
          align: "right",
          render: (value) => (
            <Tag color="green" style={{ marginInlineEnd: 0 }}>
              {formatNumber(value)}
            </Tag>
          ),
        },
        ...comparisonColumns,
        {
          title: trans("Last visit"),
          dataIndex: "lastVisitAt",
          key: "lastVisitAt",
          render: (value) => formatDate(value),
        },
      ]}
    />
  );
}

const INVENTORY_METRICS = {
  overall: {
    label: "Overall existence",
    countKey: "overallAvailableClients",
    rateKey: "overallExistenceRate",
    color: COLORS.primary,
  },
  shelf: {
    label: "Shelf availability",
    countKey: "shelfAvailableClients",
    rateKey: "shelfAvailabilityRate",
    color: COLORS.success,
  },
  warehouse: {
    label: "Warehouse availability",
    countKey: "warehouseAvailableClients",
    rateKey: "warehouseAvailabilityRate",
    color: COLORS.info,
  },
};

const compareInventoryText = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });

const compareInventoryProducts = (a, b) =>
  compareInventoryText(a?.sku, b?.sku) ||
  compareInventoryText(a?.skuCode, b?.skuCode) ||
  compareInventoryText(a?.skuId, b?.skuId);

const rankInventoryRows = (data, direction) =>
  [...arrayOrEmpty(data)]
    .sort((a, b) => {
      if (direction === "bottom") {
        return (
          numberOrZero(a?.overallExistenceRate) -
            numberOrZero(b?.overallExistenceRate) ||
          numberOrZero(b?.checkedClients) - numberOrZero(a?.checkedClients) ||
          numberOrZero(a?.shelfAvailabilityRate) -
            numberOrZero(b?.shelfAvailabilityRate) ||
          compareInventoryProducts(a, b)
        );
      }

      return (
        numberOrZero(b?.overallExistenceRate) -
          numberOrZero(a?.overallExistenceRate) ||
        numberOrZero(b?.checkedClients) - numberOrZero(a?.checkedClients) ||
        numberOrZero(b?.shelfAvailabilityRate) -
          numberOrZero(a?.shelfAvailabilityRate) ||
        compareInventoryProducts(a, b)
      );
    })
    .slice(0, 10);

const inventoryMetricValues = (row, metric) => {
  const config = INVENTORY_METRICS[metric];
  const total = optionalNumber(row?.checkedClients);
  const count = optionalNumber(row?.[config.countKey]);
  const explicitRate = optionalNumber(row?.[config.rateKey]);
  const rate =
    total > 0 && (explicitRate !== null || count !== null)
      ? Math.max(
          0,
          Math.min(
            100,
            explicitRate ?? (count / total) * 100,
          ),
        )
      : null;

  return { config, count, total, rate };
};

const inventoryMetricTooltip = (row, metric) => {
  const { config, count, total } = inventoryMetricValues(row, metric);
  if (count === null || total === null) {
    return trans("Exact count is not available");
  }
  return `${trans(config.label)}: ${formatNumber(count)} / ${formatNumber(
    total,
  )} ${trans("clients checked")}`;
};

function InventoryPercentBadge({ row, metric }) {
  const { config, rate } = inventoryMetricValues(row, metric);
  const tooltip = inventoryMetricTooltip(row, metric);

  return (
    <Tooltip title={tooltip} trigger={["hover", "focus"]}>
      <Tag
        bordered={false}
        tabIndex={0}
        aria-label={`${formatRate(rate)}. ${tooltip}`}
        style={{
          minWidth: 62,
          marginInlineEnd: 0,
          padding: "3px 9px",
          textAlign: "center",
          color: COLORS.dark,
          background: `${config.color}14`,
          fontWeight: 700,
          cursor: "help",
          direction: "ltr",
        }}
      >
        {formatRate(rate)}
      </Tag>
    </Tooltip>
  );
}

// Overall-existence change of one item versus the comparison month.
function ExistenceDelta({ row, previous, loading }) {
  const labels = React.useContext(ComparisonLabelsContext);
  if (loading) return <DeltaBadge status="loading" />;
  const hasPrevious = numberOrZero(previous?.checkedClients) > 0;
  const previousRate = hasPrevious
    ? inventoryMetricValues(previous, "overall").rate
    : null;
  const currentRate = inventoryMetricValues(row, "overall").rate;
  return (
    <DeltaBadge
      delta={rateDeltaPp(currentRate, previousRate)}
      status={hasPrevious ? undefined : "new"}
      positiveIsGood
      neutralBand={NEUTRAL_BAND_PP}
      from={hasPrevious ? formatRate(previousRate) : trans("Not checked")}
      to={formatRate(currentRate)}
      fromLabel={labels.from}
      toLabel={labels.to}
      note={trans("Overall existence")}
    />
  );
}

function InventoryTable({
  data,
  emptyDescription,
  comparisonIndex = null,
  comparisonLabel = "",
  comparisonLoading = false,
}) {
  const screens = Grid.useBreakpoint();
  const rows = arrayOrEmpty(data);
  const showComparison = Boolean(comparisonIndex) || comparisonLoading;
  const existenceDelta = (row) => (
    <ExistenceDelta
      row={row}
      previous={comparisonIndex?.get(stockItemKey(row))}
      loading={comparisonLoading}
    />
  );
  if (!rows.length)
    return (
      <EmptyPanel
        description={
          emptyDescription || "No inventory observations in this period"
        }
      />
    );

  const rowKey = (row, index) =>
    row?.formProductKey || row?.skuId || `${row?.sku || "item"}-${index}`;

  if (!screens.lg) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((row, index) => (
          <Card
            key={rowKey(row, index)}
            size="small"
            style={{ borderRadius: 11, borderColor: COLORS.border }}
            bodyStyle={{ padding: 12 }}
          >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <span>
                <Text strong style={{ color: COLORS.dark, display: "block" }}>
                  {displayText(row?.sku)}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {displayText(row?.category)}
                </Text>
              </span>

              <span style={{ display: "block", width: "100%" }}>
                <Text style={{ color: COLORS.dark, fontSize: 12 }}>
                  {trans("Overall existence")}
                </Text>
                <InventoryPercentBadge row={row} metric="overall" />
                {showComparison ? (
                  <span style={{ marginInlineStart: 8 }}>
                    {existenceDelta(row)}
                  </span>
                ) : null}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {inventoryMetricTooltip(row, "overall")}
                </Text>
              </span>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(110px, 1fr))",
                  gap: 10,
                }}
              >
                {["shelf", "warehouse"].map((metric) => {
                  const config = INVENTORY_METRICS[metric];
                  return (
                    <span
                      key={metric}
                      style={{
                        padding: 10,
                        borderRadius: 9,
                        background: COLORS.background,
                      }}
                    >
                      <Text
                        style={{
                          color: COLORS.dark,
                          display: "block",
                          fontSize: 11,
                        }}
                      >
                        {trans(config.label)}
                      </Text>
                      <InventoryPercentBadge row={row} metric={metric} />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatNumber(row?.[config.countKey])} /{" "}
                        {formatNumber(row?.checkedClients)} {trans("clients")}
                      </Text>
                    </span>
                  );
                })}
              </div>

              <Text type="secondary" style={{ fontSize: 11 }}>
                {trans("Last checked")}: {formatDate(row?.lastObservedAt)}
              </Text>
            </Space>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <Table
      size="small"
      rowKey={rowKey}
      dataSource={rows}
      pagination={false}
      scroll={{ x: showComparison ? 520 : 430, y: 480 }}
      columns={[
        {
          title: trans("Item"),
          dataIndex: "sku",
          key: "sku",
          fixed: "left",
          width: 170,
          render: (value, row) => (
            <span style={{ display: "block", minWidth: 0 }}>
              <Text
                strong
                ellipsis={{ tooltip: displayText(value) }}
                style={{ display: "block", maxWidth: 155 }}
              >
                {displayText(value)}
              </Text>
              <Text
                type="secondary"
                ellipsis
                style={{ display: "block", maxWidth: 155, fontSize: 11 }}
              >
                {displayText(row?.category)} · {formatDate(row?.lastObservedAt)}
              </Text>
            </span>
          ),
        },
        {
          title: trans("Overall existence"),
          key: "overallExistenceRate",
          align: "center",
          width: 85,
          render: (_, row) => (
            <InventoryPercentBadge row={row} metric="overall" />
          ),
        },
        ...(showComparison
          ? [
              {
                title: `${trans("vs")} ${comparisonLabel}`,
                key: "overallExistenceChange",
                align: "center",
                width: 90,
                render: (_, row) => existenceDelta(row),
              },
            ]
          : []),
        {
          title: trans("Shelf availability"),
          key: "shelfAvailabilityRate",
          align: "center",
          width: 80,
          render: (_, row) => (
            <InventoryPercentBadge row={row} metric="shelf" />
          ),
        },
        {
          title: trans("Warehouse availability"),
          key: "warehouseAvailabilityRate",
          align: "center",
          width: 95,
          render: (_, row) => (
            <InventoryPercentBadge row={row} metric="warehouse" />
          ),
        },
      ]}
    />
  );
}

const compareOptionText = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });

// Stock filter options cover both compared months, so an item seen only in
// the comparison month stays selectable.
const getComparedStockCategoryOptions = (dashboard, comparisonDashboard) =>
  comparisonDashboard
    ? unionOptions(
        getStockCategoryOptions(dashboard),
        getStockCategoryOptions(comparisonDashboard),
        (a, b) => compareOptionText(a.label, b.label),
      )
    : getStockCategoryOptions(dashboard);

const getComparedStockItemOptions = (
  dashboard,
  comparisonDashboard,
  categoryKeys,
) =>
  comparisonDashboard
    ? unionOptions(
        getStockItemOptions(dashboard, categoryKeys),
        getStockItemOptions(comparisonDashboard, categoryKeys),
        (a, b) =>
          compareOptionText(a.categoryLabel, b.categoryLabel) ||
          compareOptionText(a.label, b.label),
      )
    : getStockItemOptions(dashboard, categoryKeys);

const responseDashboardOf = (response) => response?.data || response || null;

export default function FSMRetailAnalytics({
  fromDate,
  toDate,
  refreshKey,
  comparison = null,
}) {
  const formId = process.env.REACT_APP_SHELF_FORM_ID;
  const [locationRegionSelection, setLocationRegionSelection] = React.useState([
    ALL_LOCATION_REGIONS,
  ]);
  const [locationCitySelection, setLocationCitySelection] = React.useState([
    ALL_LOCATION_CITIES,
  ]);
  const [locationClientMode, setLocationClientMode] = React.useState(
    LOCATION_CLIENT_MODE.all,
  );
  const [locationClientSelection, setLocationClientSelection] = React.useState(
    [],
  );
  const [appliedLocationFilters, setAppliedLocationFilters] = React.useState({
    regionKeys: [],
    cityKeys: [],
    clientMode: LOCATION_CLIENT_MODE.all,
    clientIds: [],
  });
  const queryRegionKeys = appliedLocationFilters.regionKeys;
  const queryCityKeys = appliedLocationFilters.cityKeys;
  const queryClientIds =
    appliedLocationFilters.clientMode === LOCATION_CLIENT_MODE.include
      ? appliedLocationFilters.clientIds
      : [];
  const queryExcludedClientIds =
    appliedLocationFilters.clientMode === LOCATION_CLIENT_MODE.exclude
      ? appliedLocationFilters.clientIds
      : [];
  // One builder for both periods guarantees identical location filters.
  const buildQueryArgs = (periodFrom, periodTo) => ({
    formId,
    fromDate: periodFrom?.format?.("YYYY-MM-DD"),
    toDate: periodTo?.format?.("YYYY-MM-DD"),
    regionKeys: queryRegionKeys.length ? queryRegionKeys : undefined,
    cityKeys: queryCityKeys.length ? queryCityKeys : undefined,
    clientIds: queryClientIds.length ? queryClientIds : undefined,
    excludeClientIds: queryExcludedClientIds.length
      ? queryExcludedClientIds
      : undefined,
  });
  const queryArgs = buildQueryArgs(fromDate, toDate);
  const comparisonArgs = comparison
    ? buildQueryArgs(comparison.fromDate, comparison.toDate)
    : null;
  const {
    data: response,
    currentData: currentPrimaryResponse,
    isLoading,
    isFetching,
    error,
    refetch: refetchPrimary,
  } = useGetRetailDashboardQuery(queryArgs, {
    skip: !formId,
    refetchOnMountOrArgChange: true,
  });
  // `currentData` (never `data`) so a previous comparison month's numbers are
  // never shown under a newly selected month while it loads. A closed month
  // is immutable, so it is only refetched after ten minutes.
  const {
    currentData: comparisonResponse,
    error: comparisonError,
    refetch: refetchComparison,
  } = useGetRetailDashboardQuery(comparisonArgs, {
    skip: !formId || !comparisonArgs,
    refetchOnMountOrArgChange: comparison?.isOpen ? true : 600,
  });
  const comparisonEnabled = Boolean(formId && comparisonArgs);
  const comparisonIsOpen = Boolean(comparison?.isOpen);
  const lastRefreshKey = React.useRef(refreshKey);
  React.useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return;
    lastRefreshKey.current = refreshKey;
    if (formId) refetchPrimary();
    // A closed comparison month does not change; refetch it only while it
    // includes today or after it failed (refresh then retries it).
    if (comparisonEnabled && (comparisonIsOpen || comparisonError)) {
      refetchComparison();
    }
    // Only a refresh tick should trigger this; the refetch callbacks are
    // stable for the mounted queries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);
  const [unvisitedSearch, setUnvisitedSearch] = React.useState("");
  const [comparisonTrendMetric, setComparisonTrendMetric] =
    React.useState("oos");
  const [moverDimension, setMoverDimension] = React.useState("category");
  const [stockCategorySelection, setStockCategorySelection] = React.useState([
    ALL_STOCK_CATEGORIES,
  ]);
  const [stockItemSelection, setStockItemSelection] = React.useState([
    ALL_STOCK_ITEMS,
  ]);
  // Follow the stock filter: items selected -> Item view, categories only ->
  // Category view, in the comparison panel's ranking.
  const stockSelectionKind = stockItemSelection.some(
    (key) => key !== ALL_STOCK_ITEMS,
  )
    ? "item"
    : stockCategorySelection.some((key) => key !== ALL_STOCK_CATEGORIES)
      ? "category"
      : null;
  React.useEffect(() => {
    if (stockSelectionKind) setMoverDimension(stockSelectionKind);
  }, [stockSelectionKind]);

  React.useEffect(() => {
    if (!response) return;

    const responseDashboard = response?.data || response || {};
    const locations = responseDashboard?.filters?.locations || {};
    const reconcile = (current, next) =>
      current.length === next.length &&
      current.every((value, index) => value === next[index])
        ? current
        : next;

    setLocationRegionSelection((current) =>
      reconcile(
        current,
        pruneAllAwareSelection(
          current,
          ALL_LOCATION_REGIONS,
          new Set(arrayOrEmpty(locations?.regions).map((row) => row.value)),
        ),
      ),
    );
    setLocationCitySelection((current) =>
      reconcile(
        current,
        pruneAllAwareSelection(
          current,
          ALL_LOCATION_CITIES,
          new Set(arrayOrEmpty(locations?.cities).map((row) => row.value)),
        ),
      ),
    );
    setLocationClientSelection((current) =>
      reconcile(
        current,
        pruneSelection(
          current,
          new Set(arrayOrEmpty(locations?.clients).map((row) => row.value)),
        ),
      ),
    );
  }, [response]);

  // Comparison options are part of the selectable set; wait for them so a
  // comparison-only selection is not pruned while that month loads.
  const comparisonOptionsPending =
    comparisonEnabled && !comparisonResponse && !comparisonError;
  const comparisonOptionsDashboard = comparisonEnabled
    ? responseDashboardOf(comparisonResponse)
    : null;

  React.useEffect(() => {
    if (!response || comparisonOptionsPending) return;

    const responseDashboard = response?.data || response || {};
    const availableKeys = new Set(
      getComparedStockCategoryOptions(
        responseDashboard,
        comparisonOptionsDashboard,
      ).map((option) => option.value),
    );
    setStockCategorySelection((currentSelection) => {
      if (currentSelection.includes(ALL_STOCK_CATEGORIES)) {
        return currentSelection.length === 1
          ? currentSelection
          : [ALL_STOCK_CATEGORIES];
      }

      const validSelection = currentSelection.filter((key) =>
        availableKeys.has(key),
      );
      if (!validSelection.length) return [ALL_STOCK_CATEGORIES];
      if (validSelection.length === currentSelection.length) {
        return currentSelection;
      }
      return validSelection;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, comparisonResponse, comparisonOptionsPending]);

  React.useEffect(() => {
    if (!response || comparisonOptionsPending) return;

    const responseDashboard = response?.data || response || {};
    const availableCategoryKeys = new Set(
      getComparedStockCategoryOptions(
        responseDashboard,
        comparisonOptionsDashboard,
      ).map((option) => option.value),
    );
    const validCategorySelection = stockCategorySelection.filter(
      (key) =>
        key === ALL_STOCK_CATEGORIES || availableCategoryKeys.has(key),
    );
    const selectedCategoryKeys = validCategorySelection.includes(
      ALL_STOCK_CATEGORIES,
    )
      ? []
      : validCategorySelection;
    const availableItemKeys = new Set(
      getComparedStockItemOptions(
        responseDashboard,
        comparisonOptionsDashboard,
        selectedCategoryKeys,
      ).map((option) => option.value),
    );

    setStockItemSelection((currentSelection) => {
      if (currentSelection.includes(ALL_STOCK_ITEMS)) {
        return currentSelection.length === 1
          ? currentSelection
          : [ALL_STOCK_ITEMS];
      }

      const validSelection = currentSelection.filter((key) =>
        availableItemKeys.has(key),
      );
      if (!validSelection.length) return [ALL_STOCK_ITEMS];
      if (validSelection.length === currentSelection.length) {
        return currentSelection;
      }
      return validSelection;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    response,
    comparisonResponse,
    comparisonOptionsPending,
    stockCategorySelection,
  ]);

  if (!formId) {
    return (
      <Alert
        type="warning"
        showIcon
        message={trans("Retail dashboard is not configured")}
        description={trans(
          "A shelf availability form must be configured first.",
        )}
      />
    );
  }

  if (isLoading) {
    return (
      <Card style={panelStyle} bodyStyle={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={trans("Failed to load retail analytics")}
        description={
          error?.data?.message ||
          error?.error ||
          trans("Please refresh the dashboard and try again.")
        }
      />
    );
  }

  const dashboard = response?.data || response || {};
  // Deltas need both periods to match what is requested: `data` can still be
  // the previous selection (dates or location filters) while a new one loads,
  // and a comparison payload resolved to a different form (non-admin
  // fallback) is not comparable.
  const matchesPeriod = (payload, args) =>
    String(payload?.period?.fromDate || payload?.period?.from || "").slice(0, 10) ===
      args?.fromDate &&
    String(payload?.period?.toDate || payload?.period?.to || "").slice(0, 10) ===
      args?.toDate;
  // `dashboard` is built from `data`; it is only safe to compare when that is
  // also the result for the current arguments.
  const primaryIsCurrent =
    Boolean(currentPrimaryResponse) && currentPrimaryResponse === response;
  const comparisonPayload = comparisonEnabled
    ? responseDashboardOf(comparisonResponse)
    : null;
  const comparisonMismatch = Boolean(
    comparisonPayload &&
      (!matchesPeriod(comparisonPayload, comparisonArgs) ||
        (dashboard?.meta?.formId &&
          comparisonPayload?.meta?.formId &&
          dashboard.meta.formId !== comparisonPayload.meta.formId)),
  );
  const comparisonDashboard =
    comparisonPayload && !comparisonMismatch ? comparisonPayload : null;
  const comparisonStatus = !comparisonEnabled
    ? null
    : comparisonError || comparisonMismatch
      ? "error"
      : comparisonDashboard && primaryIsCurrent
        ? "ready"
        : "loading";
  const comparisonReady = comparisonStatus === "ready";
  const comparisonLoading = comparisonStatus === "loading";
  const comparisonLabel = comparison?.fromDate?.format?.("MMM YYYY") || "";
  const selectedLabel = fromDate?.format?.("MMM YYYY") || "";
  const summary = dashboard?.summary || {};
  const outOfStock = dashboard?.outOfStock || {};
  const visits = dashboard?.visits || {};
  const inventory = dashboard?.inventory || {};
  const unvisitedClients = dashboard?.unvisitedClients || {};
  const unvisitedRows = arrayOrEmpty(unvisitedClients?.clients);
  const locationFilters = dashboard?.filters?.locations || {};
  const locationRegionOptions = arrayOrEmpty(locationFilters?.regions);
  const availableLocationRegionKeys = new Set(
    locationRegionOptions.map((option) => option.value),
  );
  const normalizedLocationRegionSelection = pruneAllAwareSelection(
    locationRegionSelection,
    ALL_LOCATION_REGIONS,
    availableLocationRegionKeys,
  );
  const allLocationRegionsSelected = normalizedLocationRegionSelection.includes(
    ALL_LOCATION_REGIONS,
  );
  const selectedLocationRegionKeys = allLocationRegionsSelected
    ? []
    : normalizedLocationRegionSelection;
  const locationRegionSelectOptions = [
    { value: ALL_LOCATION_REGIONS, label: trans("All regions") },
    ...locationRegionOptions.map((option) => ({
      value: option.value,
      label: option.label,
    })),
  ];

  const locationCityOptions = arrayOrEmpty(locationFilters?.cities).filter(
    (option) =>
      !selectedLocationRegionKeys.length ||
      selectedLocationRegionKeys.includes(option?.regionKey),
  );
  const availableLocationCityKeys = new Set(
    locationCityOptions.map((option) => option.value),
  );
  const normalizedLocationCitySelection = pruneAllAwareSelection(
    locationCitySelection,
    ALL_LOCATION_CITIES,
    availableLocationCityKeys,
  );
  const allLocationCitiesSelected = normalizedLocationCitySelection.includes(
    ALL_LOCATION_CITIES,
  );
  const selectedLocationCityKeys = allLocationCitiesSelected
    ? []
    : normalizedLocationCitySelection;
  const locationCitySelectOptions = [
    { value: ALL_LOCATION_CITIES, label: trans("All cities") },
    ...groupLocationOptions(
      locationCityOptions,
      "regionKey",
      "regionLabel",
    ),
  ];

  const locationClientOptions = arrayOrEmpty(locationFilters?.clients)
    .filter(
      (option) =>
        (!selectedLocationRegionKeys.length ||
          selectedLocationRegionKeys.includes(option?.regionKey)) &&
        (!selectedLocationCityKeys.length ||
          selectedLocationCityKeys.includes(option?.cityKey)),
    )
    .map((option) => ({
      ...option,
      locationLabel: locationText(option?.region, option?.city),
    }));
  const availableLocationClientIds = new Set(
    locationClientOptions.map((option) => option.value),
  );
  const normalizedLocationClientSelection = pruneSelection(
    locationClientSelection,
    availableLocationClientIds,
  );
  const selectedLocationClientIds = normalizedLocationClientSelection;
  const locationClientSelectOptions = groupLocationOptions(
    locationClientOptions,
    "cityKey",
    "locationLabel",
  );
  const hasClientLocationFilter =
    locationClientMode !== LOCATION_CLIENT_MODE.all &&
    selectedLocationClientIds.length > 0;
  const includedLocationClientCount =
    locationClientMode === LOCATION_CLIENT_MODE.exclude
      ? Math.max(
          locationClientOptions.length - selectedLocationClientIds.length,
          0,
        )
      : locationClientMode === LOCATION_CLIENT_MODE.include
        ? selectedLocationClientIds.length
        : locationClientOptions.length;
  const locationFilterActive =
    selectedLocationRegionKeys.length > 0 ||
    selectedLocationCityKeys.length > 0 ||
    hasClientLocationFilter;
  const appliedLocationFilterActive =
    appliedLocationFilters.regionKeys.length > 0 ||
    appliedLocationFilters.cityKeys.length > 0 ||
    (appliedLocationFilters.clientMode !== LOCATION_CLIENT_MODE.all &&
      appliedLocationFilters.clientIds.length > 0);
  const clientLocationSelectionComplete =
    locationClientMode === LOCATION_CLIENT_MODE.all ||
    selectedLocationClientIds.length > 0;
  const locationFiltersDirty =
    !sameSelection(
      selectedLocationRegionKeys,
      appliedLocationFilters.regionKeys,
    ) ||
    !sameSelection(selectedLocationCityKeys, appliedLocationFilters.cityKeys) ||
    locationClientMode !== appliedLocationFilters.clientMode ||
    !sameSelection(
      selectedLocationClientIds,
      appliedLocationFilters.clientIds,
    );

  const clientsAllowedByLocation = (regionKeys, cityKeys) =>
    new Set(
      arrayOrEmpty(locationFilters?.clients)
        .filter(
          (option) =>
            (!regionKeys.length || regionKeys.includes(option?.regionKey)) &&
            (!cityKeys.length || cityKeys.includes(option?.cityKey)),
        )
        .map((option) => option.value),
    );
  const handleLocationRegionChange = (nextValues) => {
    const nextRegions = nextAllAwareSelection(
      nextValues,
      ALL_LOCATION_REGIONS,
      allLocationRegionsSelected,
    );
    const nextRegionKeys = nextRegions.includes(ALL_LOCATION_REGIONS)
      ? []
      : nextRegions;
    const allowedCities = new Set(
      arrayOrEmpty(locationFilters?.cities)
        .filter(
          (option) =>
            !nextRegionKeys.length ||
            nextRegionKeys.includes(option?.regionKey),
        )
        .map((option) => option.value),
    );
    const nextCities = pruneAllAwareSelection(
      locationCitySelection,
      ALL_LOCATION_CITIES,
      allowedCities,
    );
    const nextCityKeys = nextCities.includes(ALL_LOCATION_CITIES)
      ? []
      : nextCities;

    setLocationRegionSelection(nextRegions);
    setLocationCitySelection(nextCities);
    const nextClients = pruneSelection(
      locationClientSelection,
      clientsAllowedByLocation(nextRegionKeys, nextCityKeys),
    );
    setLocationClientSelection(nextClients);
    if (!nextClients.length) setLocationClientMode(LOCATION_CLIENT_MODE.all);
  };
  const handleLocationCityChange = (nextValues) => {
    const nextCities = nextAllAwareSelection(
      nextValues,
      ALL_LOCATION_CITIES,
      allLocationCitiesSelected,
    );
    const nextCityKeys = nextCities.includes(ALL_LOCATION_CITIES)
      ? []
      : nextCities;
    setLocationCitySelection(nextCities);
    const nextClients = pruneSelection(
      locationClientSelection,
      clientsAllowedByLocation(selectedLocationRegionKeys, nextCityKeys),
    );
    setLocationClientSelection(nextClients);
    if (!nextClients.length) setLocationClientMode(LOCATION_CLIENT_MODE.all);
  };
  const handleLocationClientModeChange = (nextMode) => {
    setLocationClientMode(nextMode);
    if (nextMode === LOCATION_CLIENT_MODE.all) {
      setLocationClientSelection([]);
    }
  };
  const handleLocationClientChange = (nextValues) => {
    const nextClients = arrayOrEmpty(nextValues);
    setLocationClientSelection(nextClients);
    if (!nextClients.length) setLocationClientMode(LOCATION_CLIENT_MODE.all);
  };
  const applyLocationFilters = () => {
    if (!clientLocationSelectionComplete) return;
    setAppliedLocationFilters({
      regionKeys: [...selectedLocationRegionKeys],
      cityKeys: [...selectedLocationCityKeys],
      clientMode: locationClientMode,
      clientIds: [...selectedLocationClientIds],
    });
  };
  const clearLocationFilters = () => {
    setLocationRegionSelection([ALL_LOCATION_REGIONS]);
    setLocationCitySelection([ALL_LOCATION_CITIES]);
    setLocationClientMode(LOCATION_CLIENT_MODE.all);
    setLocationClientSelection([]);
    setAppliedLocationFilters({
      regionKeys: [],
      cityKeys: [],
      clientMode: LOCATION_CLIENT_MODE.all,
      clientIds: [],
    });
  };
  const stockCategoryOptions = getComparedStockCategoryOptions(
    dashboard,
    comparisonDashboard,
  );
  const availableStockCategoryKeys = new Set(
    stockCategoryOptions.map((option) => option.value),
  );
  const validStockCategorySelection = stockCategorySelection.filter(
    (key) =>
      key === ALL_STOCK_CATEGORIES || availableStockCategoryKeys.has(key),
  );
  const normalizedStockCategorySelection = validStockCategorySelection.length
    ? validStockCategorySelection
    : [ALL_STOCK_CATEGORIES];
  const allStockCategoriesSelected = normalizedStockCategorySelection.includes(
    ALL_STOCK_CATEGORIES,
  );
  const selectedStockCategoryKeys = allStockCategoriesSelected
    ? []
    : normalizedStockCategorySelection;
  const stockCategorySelectOptions = [
    { value: ALL_STOCK_CATEGORIES, label: trans("All categories") },
    ...stockCategoryOptions,
  ];
  const handleStockCategoryChange = (nextValues) => {
    if (!nextValues.length) {
      setStockCategorySelection([ALL_STOCK_CATEGORIES]);
      return;
    }

    const previouslyAll = allStockCategoriesSelected;
    const nextIncludesAll = nextValues.includes(ALL_STOCK_CATEGORIES);
    if (nextIncludesAll && !previouslyAll) {
      setStockCategorySelection([ALL_STOCK_CATEGORIES]);
      return;
    }

    const categoriesOnly = nextValues.filter(
      (value) => value !== ALL_STOCK_CATEGORIES,
    );
    setStockCategorySelection(
      categoriesOnly.length ? categoriesOnly : [ALL_STOCK_CATEGORIES],
    );
  };
  const stockItemOptions = getComparedStockItemOptions(
    dashboard,
    comparisonDashboard,
    selectedStockCategoryKeys,
  );
  const availableStockItemKeys = new Set(
    stockItemOptions.map((option) => option.value),
  );
  const validStockItemSelection = stockItemSelection.filter(
    (key) => key === ALL_STOCK_ITEMS || availableStockItemKeys.has(key),
  );
  const normalizedStockItemSelection = validStockItemSelection.length
    ? validStockItemSelection
    : [ALL_STOCK_ITEMS];
  const allStockItemsSelected = normalizedStockItemSelection.includes(
    ALL_STOCK_ITEMS,
  );
  const selectedStockItemKeys = allStockItemsSelected
    ? []
    : normalizedStockItemSelection;
  const stockItemSelectOptions = [
    { value: ALL_STOCK_ITEMS, label: trans("All items") },
    ...groupStockItemOptions(stockItemOptions),
  ];
  const handleStockItemChange = (nextValues) => {
    if (!nextValues.length) {
      setStockItemSelection([ALL_STOCK_ITEMS]);
      return;
    }

    const previouslyAll = allStockItemsSelected;
    const nextIncludesAll = nextValues.includes(ALL_STOCK_ITEMS);
    if (nextIncludesAll && !previouslyAll) {
      setStockItemSelection([ALL_STOCK_ITEMS]);
      return;
    }

    const itemsOnly = nextValues.filter((value) => value !== ALL_STOCK_ITEMS);
    setStockItemSelection(itemsOnly.length ? itemsOnly : [ALL_STOCK_ITEMS]);
  };
  const stockFilterActive =
    selectedStockCategoryKeys.length > 0 || selectedStockItemKeys.length > 0;
  const stockFilterSignature = [
    selectedStockCategoryKeys.length
      ? selectedStockCategoryKeys.join("|")
      : "all-categories",
    selectedStockItemKeys.length
      ? selectedStockItemKeys.join("|")
      : "all-items",
  ].join("::");
  const stockSelection = {
    categoryKeys: selectedStockCategoryKeys,
    itemKeys: selectedStockItemKeys,
  };
  const {
    filteredSnapshotItems,
    filteredStockSummary,
    filteredTrend,
    hasCompleteInventoryItems,
    filteredInventoryItems,
    filteredRepeatOOSItems,
    stockChecks,
    hasStockChecks,
    shelfAvailableRate,
    trueOOSRate,
    shelfGapRate,
  } = deriveRetailView(dashboard, stockSelection);
  // The comparison month goes through the identical stock filter.
  const comparisonView = comparisonReady
    ? deriveRetailView(comparisonDashboard, stockSelection)
    : null;
  const comparisonSummary = comparisonDashboard?.summary || {};
  const previousVisits = comparisonView
    ? createVisitLookup(comparisonDashboard)
    : null;
  const comparisonInventoryIndex = comparisonView
    ? indexByKey(comparisonView.filteredInventoryItems, stockItemKey)
    : null;
  // Exact per-item check totals for the comparison month, including items its
  // repeat out-of-stock list omitted (count <= 1).
  const comparisonItemTotals = comparisonView
    ? sumTrendItems(comparisonDashboard?.trend, stockItemKey)
    : null;
  const comparisonRepeatKeys = comparisonView
    ? new Set(arrayOrEmpty(comparisonDashboard?.repeatOOSItems).map(stockItemKey))
    : null;
  const showRepeatComparison = Boolean(comparisonView) || comparisonLoading;
  const comparisonUnvisitedIds = comparisonView
    ? new Set(
        arrayOrEmpty(comparisonDashboard?.unvisitedClients?.clients).map((row) =>
          Number(row?.clientId),
        ),
      )
    : null;
  const inventoryTopRows = hasCompleteInventoryItems
    ? rankInventoryRows(filteredInventoryItems, "top")
    : !stockFilterActive
      ? arrayOrEmpty(inventory?.top)
      : [];
  const inventoryBottomRows = hasCompleteInventoryItems
    ? rankInventoryRows(filteredInventoryItems, "bottom")
    : !stockFilterActive
      ? arrayOrEmpty(inventory?.bottom)
      : [];
  const normalizedUnvisitedSearch = unvisitedSearch.trim().toLocaleLowerCase();
  const filteredUnvisitedClients = normalizedUnvisitedSearch
    ? unvisitedRows.filter((row) =>
        [
          row?.clientName,
          row?.clientId,
          row?.type,
          row?.country,
          row?.region,
          row?.city,
        ]
          .map((value) => displayText(value))
          .join(" ")
          .toLocaleLowerCase()
          .includes(normalizedUnvisitedSearch),
      )
    : unvisitedRows;

  const clientCoverageRate = numberOrZero(summary?.clientCoverageRate);
  const stockRateDetail = trans(
    hasStockChecks
      ? "Hover for exact counts"
      : !stockFilterActive
        ? "No stock checks in this period"
        : "No stock checks for selected filters",
  );

  // Month comparison per KPI tile. Counts compare as absolute/relative change;
  // rates (snapshot semantics) compare in percentage points.
  const previousCoverageRate = comparisonView
    ? numberOrZero(comparisonSummary?.clientCoverageRate)
    : null;
  const stockSampleIsLow = comparisonView
    ? isLowSample(stockChecks, comparisonView.stockChecks)
    : false;
  const previousStockText = (rate) =>
    comparisonView?.hasStockChecks ? formatRate(rate) : trans("No stock checks");
  const rateComparison = (current, previous, positiveIsGood, previousText) => ({
    unit: "points",
    delta: rateDeltaPp(current, previous),
    positiveIsGood,
    lowSample: stockSampleIsLow,
    from: previousText,
    to: current === null ? trans("No stock checks") : formatRate(current),
  });
  const kpiComparisons = !comparisonEnabled || comparisonStatus === "error"
    ? {}
    : !comparisonView
      ? {
          "Clients visited": { loading: true },
          "Client coverage": { loading: true },
          "Shelf availability": { loading: true },
          "True out of stock": { loading: true },
          "Shelf gaps": { loading: true },
        }
      : {
          "Clients visited": {
            unit: "count",
            delta: countDelta(
              summary?.uniqueClients,
              comparisonSummary?.uniqueClients,
            ),
            positiveIsGood: true,
            from: formatNumber(comparisonSummary?.uniqueClients),
            to: formatNumber(summary?.uniqueClients),
          },
          "Client coverage": {
            ...rateComparison(
              clientCoverageRate,
              previousCoverageRate,
              true,
              formatRate(previousCoverageRate),
            ),
            lowSample: false,
          },
          "Shelf availability": rateComparison(
            shelfAvailableRate,
            comparisonView.shelfAvailableRate,
            true,
            previousStockText(comparisonView.shelfAvailableRate),
          ),
          "True out of stock": rateComparison(
            trueOOSRate,
            comparisonView.trueOOSRate,
            false,
            previousStockText(comparisonView.trueOOSRate),
          ),
          "Shelf gaps": rateComparison(
            shelfGapRate,
            comparisonView.shelfGapRate,
            false,
            previousStockText(comparisonView.shelfGapRate),
          ),
        };

  // Month comparison summary panel: every headline metric side by side.
  // Visit-based rows are never filtered by products; say so when a filter is on.
  const allProductsHint = stockFilterActive
    ? trans("All products · not affected by the item filter")
    : undefined;
  const summaryMetrics = comparisonView
    ? [
        {
          key: "clients",
          hint: allProductsHint,
          label: trans("Clients visited"),
          unit: "count",
          positiveIsGood: true,
          from: formatNumber(comparisonSummary?.uniqueClients),
          to: formatNumber(summary?.uniqueClients),
          delta: countDelta(summary?.uniqueClients, comparisonSummary?.uniqueClients),
        },
        {
          key: "visits",
          hint: allProductsHint,
          label: trans("Total visits"),
          unit: "count",
          positiveIsGood: true,
          from: formatNumber(comparisonSummary?.totalVisits),
          to: formatNumber(summary?.totalVisits),
          delta: countDelta(summary?.totalVisits, comparisonSummary?.totalVisits),
        },
        {
          key: "coverage",
          hint: allProductsHint,
          label: trans("Client coverage"),
          unit: "points",
          positiveIsGood: true,
          from: formatRate(previousCoverageRate),
          to: formatRate(clientCoverageRate),
          delta: rateDeltaPp(clientCoverageRate, previousCoverageRate),
        },
        ...[
          ["available", "Shelf availability", shelfAvailableRate, comparisonView.shelfAvailableRate, true],
          ["oos", "True out of stock", trueOOSRate, comparisonView.trueOOSRate, false],
          ["gap", "Shelf gaps", shelfGapRate, comparisonView.shelfGapRate, false],
        ].map(([key, label, current, previous, positiveIsGood]) => ({
          key,
          label: trans(label),
          hint: `${formatNumber(comparisonView.stockChecks)} → ${formatNumber(
            stockChecks,
          )} ${trans("checks")}`,
          unit: "points",
          positiveIsGood,
          lowSample: stockSampleIsLow,
          from: previousStockText(previous),
          to: hasStockChecks ? formatRate(current) : trans("No stock checks"),
          delta: rateDeltaPp(current, previous),
        })),
        {
          key: "unvisited",
          hint: allProductsHint,
          label: trans("Unvisited clients"),
          unit: "count",
          positiveIsGood: false,
          from: formatNumber(
            comparisonDashboard?.unvisitedClients?.count ??
              comparisonUnvisitedIds.size,
          ),
          to: formatNumber(unvisitedClients?.count ?? unvisitedRows.length),
          delta: countDelta(
            unvisitedClients?.count ?? unvisitedRows.length,
            comparisonDashboard?.unvisitedClients?.count ??
              comparisonUnvisitedIds.size,
          ),
        },
      ]
    : [];
  const MOVER_DIMENSION_FIELDS = {
    category: "byCategory",
    region: "byRegion",
    city: "byCity",
    channel: "byChannel",
  };
  const moverRowsFor = (payload, view) =>
    moverDimension === "item"
      ? view.filteredSnapshotItems
      : filterStockRowsBySelection(
          payload?.outOfStock?.[MOVER_DIMENSION_FIELDS[moverDimension]],
          moverDimension,
          selectedStockCategoryKeys,
          selectedStockItemKeys,
        );
  const moverLabel = (row) =>
    moverDimension === "item"
      ? displayText(row?.sku || row?.formProductKey)
      : dimensionLabel(row, moverDimension);
  const rankedMovers = comparisonView
    ? rankMovers(
        moverRowsFor(dashboard, { filteredSnapshotItems }),
        moverRowsFor(comparisonDashboard, comparisonView),
        {
          keyFn: (row) =>
            moverDimension === "item"
              ? stockItemKey(row)
              : dimensionKey(row, moverDimension),
          rateFn: (row) => metricRate(row, "available"),
          checksFn: (row) => row?.totalChecks,
          positiveIsGood: true,
          ...(stockFilterActive
            ? { limit: Infinity, minChecks: 1 }
            : { limit: 10 }),
        },
      )
    : null;
  const describeMover = (change) => ({
    ...change,
    label: moverLabel(change.row || change.previous),
    beforeText:
      numberOrZero(change.previous?.totalChecks) > 0
        ? formatRate(change.before)
        : trans("No stock checks"),
    afterText:
      numberOrZero(change.row?.totalChecks) > 0
        ? formatRate(change.after)
        : trans("No stock checks"),
    checksText: `${formatNumber(change.previous?.totalChecks)} → ${formatNumber(
      change.row?.totalChecks,
    )} ${trans("checks")}`,
    lowSample: isLowSample(change.row?.totalChecks, change.previous?.totalChecks),
  });
  const summaryMovers = rankedMovers
    ? {
        improved: rankedMovers.improved.map(describeMover),
        declined: rankedMovers.declined.map(describeMover),
        stable: rankedMovers.stable.map(describeMover),
        unmatched: stockFilterActive
          ? rankedMovers.unmatched.map(describeMover)
          : [],
        complete: stockFilterActive,
      }
    : null;
  const shortRange = (start, end) =>
    `${formatCalendarDate(start, "D MMM")} – ${formatCalendarDate(end, "D MMM YYYY")}`;
  const comparisonPeriodText = comparison
    ? [
        `${shortRange(fromDate, toDate)} ${trans("vs")} ${shortRange(
          comparison.fromDate,
          comparison.toDate,
        )}`,
        comparison.sameDays ? trans("Same days of each month") : trans("Full months"),
        trans("Months are UTC calendar months"),
      ].join(" · ")
    : "";

  const kpis = [
    {
      title: "Clients visited",
      value: summary?.uniqueClients,
      color: COLORS.primary,
      icon: <ShopOutlined />,
      detail: `${formatNumber(summary?.totalVisits)} ${trans("visits")}`,
    },
    {
      title: "Client coverage",
      value: clientCoverageRate,
      suffix: "%",
      precision: 1,
      color: COLORS.info,
      icon: <TeamOutlined />,
      detail: `${formatNumber(summary?.uniqueClients)} / ${formatNumber(
        summary?.totalKnownClients,
      )} ${trans("clients")}`,
      progress: clientCoverageRate,
    },
    {
      title: "Shelf availability",
      value: shelfAvailableRate,
      suffix: "%",
      precision: 1,
      color: COLORS.success,
      icon: <CheckCircleOutlined />,
      detail: stockRateDetail,
      tooltip: hasStockChecks
        ? metricTooltip(
            { ...filteredStockSummary, totalChecks: stockChecks },
            "available",
          )
        : undefined,
      progress: hasStockChecks ? shelfAvailableRate : undefined,
    },
    {
      title: "True out of stock",
      value: trueOOSRate,
      suffix: "%",
      precision: 1,
      color: COLORS.danger,
      icon: <StopOutlined />,
      detail: stockRateDetail,
      tooltip: hasStockChecks
        ? metricTooltip(
            { ...filteredStockSummary, totalChecks: stockChecks },
            "oos",
          )
        : undefined,
      progress: hasStockChecks ? trueOOSRate : undefined,
    },
    {
      title: "Shelf gaps",
      value: shelfGapRate,
      suffix: "%",
      precision: 1,
      color: COLORS.warning,
      icon: <WarningOutlined />,
      detail: stockRateDetail,
      tooltip: hasStockChecks
        ? metricTooltip(
            { ...filteredStockSummary, totalChecks: stockChecks },
            "gap",
          )
        : undefined,
      progress: hasStockChecks ? shelfGapRate : undefined,
    },
  ];

  // Day-of-month comparison chart. Any single calendar month is returned with
  // daily buckets, so days align one-to-one. One metric at a time on one
  // axis: selected month solid green, comparison month dashed blue.
  const trendComparison =
    comparisonView &&
    dashboard?.period?.granularity === "daily" &&
    comparisonDashboard?.period?.granularity === "daily"
      ? alignTrendByDayOfMonth(filteredTrend, comparisonView.filteredTrend)
      : null;
  const COMPARISON_TREND_METRICS = {
    oos: { label: "Out-of-stock rate", rate: "oos" },
    available: { label: "Shelf availability rate", rate: "available" },
    visits: { label: "Visits" },
  };
  const comparisonMetric =
    COMPARISON_TREND_METRICS[comparisonTrendMetric] ||
    COMPARISON_TREND_METRICS.oos;
  const comparisonPointValue = (item) => {
    if (!item) return null;
    return comparisonMetric.rate
      ? metricRate(item, comparisonMetric.rate)
      : numberOrZero(item?.visits);
  };
  const comparisonPointText = (item, value) => {
    if (!item) return trans("No data");
    if (!comparisonMetric.rate) return formatNumber(value);
    return value === null
      ? trans("No stock checks")
      : `${formatRate(value)} · ${metricTooltip(item, comparisonMetric.rate)}`;
  };
  const comparisonTrendSeries = trendComparison
    ? [
        {
          name: selectedLabel,
          data: trendComparison.current.map(comparisonPointValue),
        },
        {
          name: comparisonLabel,
          data: trendComparison.previous.map(comparisonPointValue),
        },
      ]
    : [];
  const comparisonTrendOptions = trendComparison
    ? {
        chart: {
          type: "line",
          toolbar: { show: false },
          fontFamily: "'Cairo', sans-serif",
          animations: { enabled: false },
          zoom: { enabled: false },
        },
        colors: [COLORS.primary, COLORS.info],
        stroke: { curve: "straight", width: [2, 2], dashArray: [0, 6] },
        markers: { size: 4, strokeWidth: 2, strokeColors: "#ffffff", hover: { size: 6 } },
        dataLabels: { enabled: false },
        grid: { borderColor: COLORS.border, strokeDashArray: 4 },
        xaxis: {
          categories: trendComparison.days.map(String),
          title: { text: trans("Day of month") },
          labels: { style: { colors: COLORS.neutral, fontSize: "11px" } },
          tooltip: { enabled: false },
        },
        yaxis: {
          min: 0,
          ...(comparisonMetric.rate ? { max: 100, tickAmount: 4 } : { forceNiceScale: true }),
          labels: {
            formatter: (value) =>
              comparisonMetric.rate
                ? `${formatNumber(value)}%`
                : formatNumber(value),
            style: { colors: COLORS.neutral, fontSize: "11px" },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          labels: { colors: COLORS.dark },
          markers: { strokeWidth: 0 },
        },
        tooltip: {
          shared: true,
          intersect: false,
          x: {
            formatter: (value, { dataPointIndex } = {}) => {
              const current = trendComparison.current[dataPointIndex];
              const previous = trendComparison.previous[dataPointIndex];
              return [
                `${trans("Day")} ${value}`,
                current ? formatCalendarDate(current.date, "DD MMM") : null,
                previous ? formatCalendarDate(previous.date, "DD MMM") : null,
              ]
                .filter(Boolean)
                .join(" · ");
            },
          },
          y: [
            {
              formatter: (value, { dataPointIndex }) =>
                comparisonPointText(trendComparison.current[dataPointIndex], value),
            },
            {
              formatter: (value, { dataPointIndex }) =>
                comparisonPointText(trendComparison.previous[dataPointIndex], value),
            },
          ],
        },
      }
    : null;

  const trendOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      fontFamily: "'Cairo', sans-serif",
      animations: { enabled: true },
    },
    colors: [COLORS.primary, COLORS.danger, COLORS.success],
    stroke: { curve: "smooth", width: [0, 3, 3] },
    markers: { size: [0, 3, 3], strokeWidth: 0 },
    plotOptions: {
      bar: { columnWidth: "48%", borderRadius: 3 },
    },
    dataLabels: { enabled: false },
    grid: { borderColor: COLORS.border, strokeDashArray: 4 },
    xaxis: {
      categories: filteredTrend.map((item) =>
        formatCalendarDate(item?.date, "DD MMM"),
      ),
      labels: { style: { colors: COLORS.neutral, fontSize: "11px" } },
    },
    yaxis: [
      {
        seriesName: trans("Visits"),
        min: 0,
        forceNiceScale: true,
        title: { text: trans("Visits") },
        labels: {
          formatter: (value) => formatNumber(value),
          style: { colors: COLORS.neutral, fontSize: "11px" },
        },
      },
      {
        seriesName: trans("Out-of-stock rate"),
        opposite: true,
        min: 0,
        max: 100,
        tickAmount: 4,
        title: { text: trans("Percentage") },
        labels: {
          formatter: (value) => `${formatNumber(value)}%`,
          style: { colors: COLORS.neutral, fontSize: "11px" },
        },
      },
      {
        seriesName: trans("Shelf availability rate"),
        show: false,
        min: 0,
        max: 100,
        tickAmount: 4,
      },
    ],
    legend: {
      position: "top",
      horizontalAlign: "right",
      labels: { colors: COLORS.dark },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        {
          formatter: (value) => formatNumber(value),
        },
        {
          formatter: (value, { dataPointIndex }) =>
            `${formatRate(value)} · ${metricTooltip(
              filteredTrend[dataPointIndex],
              "oos",
            )}`,
        },
        {
          formatter: (value, { dataPointIndex }) =>
            `${formatRate(value)} · ${metricTooltip(
              filteredTrend[dataPointIndex],
              "available",
            )}`,
        },
      ],
    },
  };
  const trendSeries = [
    {
      name: trans("Visits"),
      type: "column",
      data: filteredTrend.map((item) => numberOrZero(item?.visits)),
    },
    {
      name: trans("Out-of-stock rate"),
      type: "line",
      data: filteredTrend.map((item) => metricRate(item, "oos")),
    },
    {
      name: trans("Shelf availability rate"),
      type: "line",
      data: filteredTrend.map((item) => metricRate(item, "available")),
    },
  ];

  return (
    <ComparisonLabelsContext.Provider
      value={{ from: comparisonLabel, to: selectedLabel }}
    >
    <section aria-label={trans("Retail analytics")}>
      <Card
        style={{
          ...panelStyle,
          marginBottom: 16,
          overflow: "hidden",
          background:
            "linear-gradient(120deg, rgba(15,105,54,0.10), rgba(2,132,199,0.06) 58%, #ffffff)",
        }}
        bodyStyle={{ padding: "20px 22px" }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 12]}>
          <Col>
            <Space size={12} align="start">
              <span
                style={{
                  width: 46,
                  height: 46,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 12,
                  color: "#fff",
                  background: COLORS.primary,
                  fontSize: 22,
                  boxShadow: "0 8px 18px rgba(15,105,54,0.22)",
                }}
              >
                <BarChartOutlined />
              </span>
              <span>
                <Title level={4} style={{ margin: 0, color: COLORS.dark }}>
                  {trans("Retail analytics")}
                </Title>
                <Text type="secondary">
                  {trans(
                    "Shelf availability, inventory and client visit insights",
                  )}
                </Text>
              </span>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              {isFetching ? (
                <Tag color="processing">{trans("Updating")}</Tag>
              ) : null}
              <Tag icon={<CalendarOutlined />} color="green">
                {formatCalendarDate(
                  dashboard?.period?.from ||
                    dashboard?.period?.fromDate ||
                    fromDate,
                )}{" "}
                –{" "}
                {formatCalendarDate(
                  dashboard?.period?.to || dashboard?.period?.toDate || toDate,
                )}
              </Tag>
              {comparisonEnabled && comparisonStatus === "error" ? (
                <Space size={4}>
                  <Tooltip
                    title={
                      comparisonError?.data?.message ||
                      comparisonError?.error ||
                      (comparisonMismatch
                        ? trans(
                            "Comparison data did not match the selected scope",
                          )
                        : trans("Please refresh the dashboard and try again."))
                    }
                  >
                    <Tag color="error" icon={<WarningOutlined />}>
                      {trans("Comparison unavailable")}
                    </Tag>
                  </Tooltip>
                  <Button size="small" onClick={() => refetchComparison()}>
                    {trans("Retry")}
                  </Button>
                </Space>
              ) : comparisonEnabled ? (
                <Tooltip
                  title={[
                    comparison?.sameDays
                      ? trans("Same days of each month")
                      : null,
                    trans("Months are UTC calendar months"),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                >
                  <Tag
                    icon={<CalendarOutlined />}
                    color={comparisonLoading ? "processing" : "blue"}
                  >
                    {trans("vs")} {formatCalendarDate(comparison.fromDate)} –{" "}
                    {formatCalendarDate(comparison.toDate)}
                    {comparison?.sameDays ? ` · ${trans("same days")}` : ""}
                  </Tag>
                </Tooltip>
              ) : null}
              {summary?.latestSubmissionAt ? (
                <Tooltip title={trans("Latest submission in this period")}>
                  <Tag>
                    {formatDate(
                      summary.latestSubmissionAt,
                      "DD MMM YYYY, HH:mm",
                    )}
                  </Tag>
                </Tooltip>
              ) : null}
            </Space>
          </Col>
        </Row>
      </Card>

      <Card
        role="region"
        aria-labelledby="retail-location-filter-title"
        style={{
          ...panelStyle,
          marginBottom: 12,
          borderColor: "#bfdbfe",
          background: "#f8fbff",
        }}
        bodyStyle={{ padding: "14px 16px" }}
      >
        <Row align="middle" gutter={[20, 12]}>
          <Col xs={24} lg={7}>
            <Space size={11} align="start">
              <span
                aria-hidden="true"
                style={{
                  width: 36,
                  height: 36,
                  flex: "0 0 36px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 10,
                  color: COLORS.info,
                  background: `${COLORS.info}12`,
                  fontSize: 17,
                }}
              >
                <EnvironmentOutlined />
              </span>
              <span>
                <Text
                  id="retail-location-filter-title"
                  strong
                  style={{ display: "block", color: COLORS.dark }}
                >
                  {trans("Location filters")}
                </Text>
                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                  {trans(
                    "Filters all retail widgets, including visits, coverage and stock metrics.",
                  )}
                </Text>
                <Space wrap size={[6, 4]} style={{ marginTop: 7 }}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={applyLocationFilters}
                    loading={isFetching && !isLoading}
                    disabled={
                      !locationFiltersDirty || !clientLocationSelectionComplete
                    }
                  >
                    {trans("Apply filters")}
                  </Button>
                  {locationFiltersDirty ? (
                    <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                      {trans("Not applied")}
                    </Tag>
                  ) : null}
                  {locationFilterActive ||
                  appliedLocationFilterActive ||
                  locationFiltersDirty ? (
                    <Button
                      type="link"
                      size="small"
                      onClick={clearLocationFilters}
                      style={{ height: "auto", padding: 0 }}
                    >
                      {trans("Clear location filters")}
                    </Button>
                  ) : null}
                </Space>
              </span>
            </Space>
          </Col>
          <Col xs={24} lg={17}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <Text strong style={{ color: COLORS.dark, fontSize: 12 }}>
                    {trans("Region")}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {allLocationRegionsSelected
                      ? `${trans("All regions")} (${formatNumber(
                          locationRegionOptions.length,
                        )})`
                      : `${formatNumber(selectedLocationRegionKeys.length)} / ${formatNumber(
                          locationRegionOptions.length,
                        )} ${trans("Regions")}`}
                  </Text>
                </div>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  aria-label={trans("Filter by regions")}
                  value={normalizedLocationRegionSelection}
                  onChange={handleLocationRegionChange}
                  options={locationRegionSelectOptions}
                  maxTagCount={1}
                  maxTagPlaceholder={(omittedValues) =>
                    `+${omittedValues.length}`
                  }
                  allowClear={!allLocationRegionsSelected}
                  disabled={!locationRegionOptions.length}
                  notFoundContent={trans("No matching regions")}
                  style={{ width: "100%", minWidth: 0 }}
                />
              </Col>
              <Col xs={24} md={8}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <Text strong style={{ color: COLORS.dark, fontSize: 12 }}>
                    {trans("City")}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {allLocationCitiesSelected
                      ? `${trans("All cities")} (${formatNumber(
                          locationCityOptions.length,
                        )})`
                      : `${formatNumber(selectedLocationCityKeys.length)} / ${formatNumber(
                          locationCityOptions.length,
                        )} ${trans("Cities")}`}
                  </Text>
                </div>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  aria-label={trans("Filter by cities")}
                  value={normalizedLocationCitySelection}
                  onChange={handleLocationCityChange}
                  options={locationCitySelectOptions}
                  maxTagCount={1}
                  maxTagPlaceholder={(omittedValues) =>
                    `+${omittedValues.length}`
                  }
                  allowClear={!allLocationCitiesSelected}
                  disabled={!locationCityOptions.length}
                  notFoundContent={trans("No matching cities")}
                  style={{ width: "100%", minWidth: 0 }}
                />
              </Col>
              <Col xs={24} md={8}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <Text strong style={{ color: COLORS.dark, fontSize: 12 }}>
                    {trans("Client location / Store")}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {locationClientMode === LOCATION_CLIENT_MODE.all
                      ? `${trans("All client locations")} (${formatNumber(
                          locationClientOptions.length,
                        )})`
                      : !selectedLocationClientIds.length
                        ? trans(
                            locationClientMode === LOCATION_CLIENT_MODE.exclude
                              ? "Choose locations to exclude"
                              : "Choose locations to include",
                          )
                        : locationClientMode === LOCATION_CLIENT_MODE.exclude
                          ? `${trans("All except")} ${formatNumber(
                              selectedLocationClientIds.length,
                            )} · ${formatNumber(
                              includedLocationClientCount,
                            )} / ${formatNumber(
                              locationClientOptions.length,
                            )} ${trans("Clients")}`
                          : `${formatNumber(
                              includedLocationClientCount,
                            )} / ${formatNumber(
                              locationClientOptions.length,
                            )} ${trans("Clients")}`}
                  </Text>
                </div>
                <Space.Compact block>
                  <Select
                    aria-label={trans("Client selection mode")}
                    value={locationClientMode}
                    onChange={handleLocationClientModeChange}
                    options={[
                      { value: LOCATION_CLIENT_MODE.all, label: trans("All") },
                      {
                        value: LOCATION_CLIENT_MODE.include,
                        label: trans("Include only"),
                      },
                      {
                        value: LOCATION_CLIENT_MODE.exclude,
                        label: trans("Exclude"),
                      },
                    ]}
                    popupMatchSelectWidth={false}
                    style={{
                      width:
                        locationClientMode === LOCATION_CLIENT_MODE.all
                          ? "100%"
                          : 122,
                      flex:
                        locationClientMode === LOCATION_CLIENT_MODE.all
                          ? "1 1 auto"
                          : "0 0 122px",
                    }}
                  />
                  {locationClientMode !== LOCATION_CLIENT_MODE.all ? (
                    <Select
                      mode="multiple"
                      showSearch
                      autoClearSearchValue={false}
                      optionFilterProp="label"
                      aria-label={trans(
                        locationClientMode === LOCATION_CLIENT_MODE.exclude
                          ? "Choose locations to exclude"
                          : "Choose locations to include",
                      )}
                      placeholder={trans(
                        locationClientMode === LOCATION_CLIENT_MODE.exclude
                          ? "Choose locations to exclude"
                          : "Choose locations to include",
                      )}
                      value={normalizedLocationClientSelection}
                      onChange={handleLocationClientChange}
                      options={locationClientSelectOptions}
                      maxTagCount={0}
                      maxTagPlaceholder={(omittedValues) => (
                        <Tag
                          color={
                            locationClientMode === LOCATION_CLIENT_MODE.exclude
                              ? "red"
                              : "blue"
                          }
                          style={{
                            margin: 0,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {locationClientMode ===
                          LOCATION_CLIENT_MODE.exclude ? (
                            <StopOutlined style={{ marginInlineEnd: 4 }} />
                          ) : null}
                          {formatNumber(omittedValues.length)} {trans(
                            locationClientMode === LOCATION_CLIENT_MODE.exclude
                              ? "excluded"
                              : "selected",
                          )}
                        </Tag>
                      )}
                      allowClear={selectedLocationClientIds.length > 0}
                      disabled={!locationClientOptions.length}
                      notFoundContent={trans("No matching client locations")}
                      style={{ flex: "1 1 auto", minWidth: 0, height: 32 }}
                    />
                  ) : null}
                </Space.Compact>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <Card
        role="region"
        aria-labelledby="retail-stock-filter-title"
        style={{
          ...panelStyle,
          marginBottom: 16,
          borderColor: "#cfe8d8",
          background: "#fbfefc",
        }}
        bodyStyle={{ padding: "14px 16px" }}
      >
        <Row align="middle" gutter={[20, 12]}>
          <Col xs={24} lg={7}>
            <Space size={11} align="start">
              <span
                aria-hidden="true"
                style={{
                  width: 36,
                  height: 36,
                  flex: "0 0 36px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 10,
                  color: COLORS.primary,
                  background: `${COLORS.primary}12`,
                  fontSize: 17,
                }}
              >
                <FilterOutlined />
              </span>
              <span>
                <Text
                  id="retail-stock-filter-title"
                  strong
                  style={{ display: "block", color: COLORS.dark }}
                >
                  {trans("Stock filters")}
                </Text>
                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                  {trans(
                    "Filter stock widgets by category and item. Visit metrics remain based on completed tasks.",
                  )}
                </Text>
              </span>
            </Space>
          </Col>
          <Col xs={24} lg={17}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    minWidth: 0,
                    marginBottom: 6,
                  }}
                >
                  <Text strong style={{ color: COLORS.dark, fontSize: 12 }}>
                    {trans("Filter by categories")}
                  </Text>
                  <Text
                    type="secondary"
                    aria-live="polite"
                    style={{ fontSize: 11, marginInlineStart: "auto" }}
                  >
                    {allStockCategoriesSelected
                      ? `${trans("All categories")} (${formatNumber(
                          stockCategoryOptions.length,
                        )})`
                      : `${formatNumber(selectedStockCategoryKeys.length)} / ${formatNumber(
                          stockCategoryOptions.length,
                        )} ${trans("Categories")}`}
                  </Text>
                </div>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  aria-label={trans("Filter by categories")}
                  value={normalizedStockCategorySelection}
                  onChange={handleStockCategoryChange}
                  options={stockCategorySelectOptions}
                  maxTagCount={1}
                  maxTagPlaceholder={(omittedValues) =>
                    `+${omittedValues.length}`
                  }
                  allowClear={!allStockCategoriesSelected}
                  disabled={!stockCategoryOptions.length}
                  style={{ width: "100%", minWidth: 0 }}
                />
              </Col>
              <Col xs={24} md={12}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    minWidth: 0,
                    marginBottom: 6,
                  }}
                >
                  <Text strong style={{ color: COLORS.dark, fontSize: 12 }}>
                    {trans("Filter by items")}
                  </Text>
                  <Text
                    type="secondary"
                    aria-live="polite"
                    style={{ fontSize: 11, marginInlineStart: "auto" }}
                  >
                    {allStockItemsSelected
                      ? `${trans("All items")} (${formatNumber(
                          stockItemOptions.length,
                        )})`
                      : `${formatNumber(selectedStockItemKeys.length)} / ${formatNumber(
                          stockItemOptions.length,
                        )} ${trans("Items")}`}
                  </Text>
                </div>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  aria-label={trans("Filter by items")}
                  value={normalizedStockItemSelection}
                  onChange={handleStockItemChange}
                  options={stockItemSelectOptions}
                  maxTagCount={1}
                  maxTagPlaceholder={(omittedValues) =>
                    `+${omittedValues.length}`
                  }
                  allowClear={!allStockItemsSelected}
                  disabled={!stockItemOptions.length}
                  notFoundContent={trans("No matching items")}
                  style={{ width: "100%", minWidth: 0 }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      {comparisonEnabled && comparisonStatus !== "error" ? (
        <RetailComparisonSummary
          status={comparisonView ? "ready" : "loading"}
          selectedLabel={selectedLabel}
          comparisonLabel={comparisonLabel}
          periodText={comparisonPeriodText}
          metrics={summaryMetrics}
          moverDimension={moverDimension}
          onMoverDimensionChange={setMoverDimension}
          moverDimensionOptions={[
            { value: "category", label: trans("Category") },
            { value: "region", label: trans("Region") },
            { value: "city", label: trans("City") },
            { value: "channel", label: trans("Channel") },
            { value: "item", label: trans("Item") },
          ]}
          movers={summaryMovers}
        />
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {kpis.map((kpi) => (
          <Tooltip
            key={kpi.title}
            title={kpi.tooltip}
            placement="top"
            trigger={["hover", "focus"]}
          >
            <Card
              tabIndex={kpi.tooltip ? 0 : undefined}
              aria-label={
                kpi.tooltip
                  ? `${trans(kpi.title)}: ${formatRate(kpi.value)}. ${kpi.tooltip}`
                  : undefined
              }
              style={{
                ...panelStyle,
                cursor: kpi.tooltip ? "help" : undefined,
              }}
              bodyStyle={{ padding: 16 }}
            >
              <Space
                align="start"
                style={{ width: "100%", justifyContent: "space-between" }}
              >
                <span>
                  <Text style={{ color: COLORS.dark, fontSize: 12 }}>
                    {trans(kpi.title)}
                  </Text>
                  <Statistic
                    value={kpi.value === null ? "—" : numberOrZero(kpi.value)}
                    suffix={kpi.value === null ? undefined : kpi.suffix}
                    precision={kpi.value === null ? undefined : kpi.precision}
                    formatter={
                      kpi.suffix === "%" &&
                      optionalNumber(kpi.value) > 0 &&
                      optionalNumber(kpi.value) < 0.1
                        ? () => "<0.1"
                        : undefined
                    }
                    valueStyle={{
                      color: COLORS.dark,
                      fontSize: 25,
                      fontWeight: 700,
                    }}
                  />
                </span>
                <span
                  style={{
                    width: 38,
                    height: 38,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 10,
                    color: kpi.color,
                    background: `${kpi.color}14`,
                    fontSize: 18,
                  }}
                >
                  {kpi.icon}
                </span>
              </Space>
              {kpi.progress !== undefined ? (
                <Progress
                  percent={Math.max(0, Math.min(100, kpi.progress))}
                  showInfo={false}
                  strokeColor={kpi.color}
                  trailColor="#edf2f7"
                  size="small"
                  style={{ margin: "9px 0 2px" }}
                />
              ) : (
                <div style={{ height: 13 }} />
              )}
              <Text style={{ color: COLORS.dark, fontSize: 12 }}>
                {kpi.detail}
              </Text>
              {kpiComparisons[kpi.title] ? (
                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "baseline",
                    gap: "2px 6px",
                  }}
                >
                  {kpiComparisons[kpi.title].loading ? (
                    <DeltaBadge status="loading" />
                  ) : (
                    <DeltaBadge
                      variant="detailed"
                      unit={kpiComparisons[kpi.title].unit}
                      delta={kpiComparisons[kpi.title].delta}
                      positiveIsGood={kpiComparisons[kpi.title].positiveIsGood}
                      neutralBand={
                        kpiComparisons[kpi.title].unit === "points"
                          ? NEUTRAL_BAND_PP
                          : 0
                      }
                      lowSample={kpiComparisons[kpi.title].lowSample}
                      from={kpiComparisons[kpi.title].from}
                      to={kpiComparisons[kpi.title].to}
                      fromLabel={comparisonLabel}
                      toLabel={selectedLabel}
                    />
                  )}
                </div>
              ) : null}
            </Card>
          </Tooltip>
        ))}
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={14}>
          <Card
            style={panelStyle}
            title={
              <PanelTitle
                icon={<BarChartOutlined />}
                title="Retail activity trend"
                subtitle="Visits and stock-health percentages over time"
              />
            }
            bodyStyle={{ padding: 16 }}
          >
            {trendComparison ? (
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Segmented
                  value={comparisonTrendMetric}
                  onChange={setComparisonTrendMetric}
                  options={[
                    { value: "oos", label: trans("Out-of-stock rate") },
                    { value: "available", label: trans("Shelf availability rate") },
                    { value: "visits", label: trans("Visits") },
                  ]}
                />
                <ReactApexChart
                  key={`${stockFilterSignature}::${comparison?.monthKey}::${comparisonTrendMetric}`}
                  type="line"
                  height={300}
                  options={comparisonTrendOptions}
                  series={comparisonTrendSeries}
                />
              </Space>
            ) : filteredTrend.length ? (
              <ReactApexChart
                key={stockFilterSignature}
                type="line"
                height={320}
                options={trendOptions}
                series={trendSeries}
              />
            ) : (
              <EmptyPanel />
            )}
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card
            style={panelStyle}
            title={
              <PanelTitle
                icon={<TeamOutlined />}
                title="Client visit frequency"
                subtitle="Most and least visited clients"
              />
            }
            bodyStyle={{ padding: "8px 16px 16px" }}
          >
            <Tabs
              items={[
                {
                  key: "most",
                  label: trans("Most visited"),
                  children: (
                    <VisitsTable
                      data={visits?.mostVisited}
                      previousVisits={previousVisits}
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                    />
                  ),
                },
                {
                  key: "least",
                  label: trans("Least visited"),
                  children: (
                    <VisitsTable
                      data={visits?.leastVisited}
                      previousVisits={previousVisits}
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} align="stretch" style={{ marginBottom: 16 }}>
        <Col xs={24} xl={12} style={{ display: "flex" }}>
          <Card
            style={{ ...panelStyle, width: "100%" }}
            title={
              <PanelTitle
                icon={<EnvironmentOutlined />}
                title="Stock health by dimension"
                subtitle="Stock status from each client's latest submitted stock report"
              />
            }
            bodyStyle={{ padding: "8px 16px 16px" }}
          >
            <Alert
              type="info"
              showIcon
              message={trans(
                "Blank or zero quantity is unavailable. Hover a percentage or focus the stock mix for exact counts.",
              )}
              style={{ margin: "8px 0" }}
            />
            <Tabs
              items={[
                {
                  key: "category",
                  label: trans("By category"),
                  children: (
                    <DistributionPanel
                      data={outOfStock?.byCategory}
                      dimension="category"
                      selectedCategoryKeys={selectedStockCategoryKeys}
                      selectedItemKeys={selectedStockItemKeys}
                      comparisonData={
                        comparisonView
                          ? comparisonDashboard?.outOfStock?.byCategory
                          : null
                      }
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                    />
                  ),
                },
                {
                  key: "item",
                  label: trans("By item"),
                  children: (
                    <DistributionPanel
                      data={buildItemStockRows(outOfStock)}
                      dimension="item"
                      selectedCategoryKeys={selectedStockCategoryKeys}
                      selectedItemKeys={selectedStockItemKeys}
                      comparisonData={
                        comparisonView
                          ? buildItemStockRows(comparisonDashboard?.outOfStock)
                          : null
                      }
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                    />
                  ),
                },
                {
                  key: "region",
                  label: trans("By region"),
                  children: (
                    <DistributionPanel
                      data={outOfStock?.byRegion}
                      dimension="region"
                      selectedCategoryKeys={selectedStockCategoryKeys}
                      selectedItemKeys={selectedStockItemKeys}
                      comparisonData={
                        comparisonView
                          ? comparisonDashboard?.outOfStock?.byRegion
                          : null
                      }
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                    />
                  ),
                },
                {
                  key: "city",
                  label: trans("By city"),
                  children: (
                    <DistributionPanel
                      data={outOfStock?.byCity}
                      dimension="city"
                      selectedCategoryKeys={selectedStockCategoryKeys}
                      selectedItemKeys={selectedStockItemKeys}
                      comparisonData={
                        comparisonView
                          ? comparisonDashboard?.outOfStock?.byCity
                          : null
                      }
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                    />
                  ),
                },
                {
                  key: "channel",
                  label: trans("By channel"),
                  children: (
                    <DistributionPanel
                      data={outOfStock?.byChannel}
                      dimension="channel"
                      selectedCategoryKeys={selectedStockCategoryKeys}
                      selectedItemKeys={selectedStockItemKeys}
                      comparisonData={
                        comparisonView
                          ? comparisonDashboard?.outOfStock?.byChannel
                          : null
                      }
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} xl={12} style={{ display: "flex" }}>
          <Card
            style={{ ...panelStyle, width: "100%" }}
            title={
              <PanelTitle
                icon={<DatabaseOutlined />}
                title="Inventory existence"
                subtitle="Share of checked clients where each item was found in their latest submitted stock report"
              />
            }
            bodyStyle={{ padding: "8px 16px 16px" }}
          >
            <Alert
              type="info"
              showIcon
              message={trans(
                "Warehouse availability is the share of checked clients with at least one container. Overall existence means shelf or warehouse presence; rates may overlap.",
              )}
              style={{ margin: "8px 0" }}
            />
            <Tabs
              items={[
                {
                  key: "top",
                  label: trans("Highest existence"),
                  children: (
                    <InventoryTable
                      data={inventoryTopRows}
                      comparisonIndex={comparisonInventoryIndex}
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                      emptyDescription={
                        !stockFilterActive
                          ? undefined
                          : "No inventory observations for selected filters"
                      }
                    />
                  ),
                },
                {
                  key: "bottom",
                  label: trans("Lowest existence"),
                  children: (
                    <InventoryTable
                      data={inventoryBottomRows}
                      comparisonIndex={comparisonInventoryIndex}
                      comparisonLabel={comparisonLabel}
                      comparisonLoading={comparisonLoading}
                      emptyDescription={
                        !stockFilterActive
                          ? undefined
                          : "No inventory observations for selected filters"
                      }
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card
            style={panelStyle}
            title={
              <PanelTitle
                icon={<WarningOutlined />}
                title="Repeat out-of-stock items"
                subtitle="Items repeatedly unavailable across visits"
              />
            }
            bodyStyle={{ padding: 16 }}
          >
            {filteredRepeatOOSItems.length ? (
              <Table
                key={`${stockFilterSignature}::${showRepeatComparison ? "compare" : "single"}`}
                size="small"
                rowKey={(row, index) =>
                  row?.formProductKey ||
                  row?.skuId ||
                  `${row?.sku || "item"}-${index}`
                }
                dataSource={filteredRepeatOOSItems}
                pagination={
                  filteredRepeatOOSItems.length > 10
                    ? {
                        defaultPageSize: 10,
                        size: "small",
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "25", "50"],
                        showTotal: (total, range) =>
                          `${formatNumber(range[0])}–${formatNumber(
                            range[1],
                          )} / ${formatNumber(total)} ${trans("Items")}`,
                      }
                    : false
                }
                scroll={{ x: showRepeatComparison ? 760 : 600 }}
                columns={[
                  {
                    title: trans("Item"),
                    dataIndex: "sku",
                    key: "sku",
                    render: (value) => <Text strong>{displayText(value)}</Text>,
                  },
                  {
                    title: trans("Category"),
                    dataIndex: "category",
                    key: "category",
                    render: (value) => displayText(value),
                  },
                  {
                    title: trans("Out-of-stock rate"),
                    key: "trueOOSRate",
                    align: "right",
                    defaultSortOrder: "descend",
                    sorter: (a, b) =>
                      (metricRate(a, "oos") ?? -1) -
                      (metricRate(b, "oos") ?? -1),
                    render: (_, row) => (
                      <PercentMetric
                        row={row}
                        metric="oos"
                        color={COLORS.danger}
                      />
                    ),
                  },
                  ...(showRepeatComparison
                    ? [
                        {
                          title: `${trans("vs")} ${comparisonLabel}`,
                          key: "trueOOSRateChange",
                          align: "right",
                          render: (_, row) => {
                            if (!comparisonView) {
                              return <DeltaBadge status="loading" />;
                            }
                            const key = stockItemKey(row);
                            const previous = comparisonItemTotals.get(key);
                            const hasPrevious =
                              numberOrZero(previous?.totalChecks) > 0;
                            const status = comparisonRepeatKeys.has(key)
                              ? "Repeat in both"
                              : hasPrevious
                                ? "New repeat"
                                : "Not checked";
                            return (
                              <Space direction="vertical" size={2} align="end">
                                <DeltaBadge
                                  delta={rateDeltaPp(
                                    metricRate(row, "oos"),
                                    hasPrevious ? previous.trueOOSRate : null,
                                  )}
                                  positiveIsGood={false}
                                  neutralBand={NEUTRAL_BAND_PP}
                                  lowSample={
                                    hasPrevious &&
                                    isLowSample(
                                      row?.totalChecks,
                                      previous.totalChecks,
                                    )
                                  }
                                  from={
                                    hasPrevious
                                      ? formatRate(previous.trueOOSRate)
                                      : trans("No stock checks")
                                  }
                                  to={formatRate(metricRate(row, "oos"))}
                                  fromLabel={comparisonLabel}
                                  toLabel={selectedLabel}
                                  note={trans("Out-of-stock rate")}
                                />
                                <Tag
                                  color={
                                    status === "Repeat in both"
                                      ? "red"
                                      : status === "New repeat"
                                        ? "orange"
                                        : undefined
                                  }
                                  style={{ marginInlineEnd: 0, fontSize: 11 }}
                                >
                                  {trans(status)}
                                </Tag>
                              </Space>
                            );
                          },
                        },
                      ]
                    : []),
                  {
                    title: trans("Clients"),
                    dataIndex: "uniqueClients",
                    key: "uniqueClients",
                    align: "right",
                    render: (value) => formatNumber(value),
                  },
                ]}
              />
            ) : (
              <EmptyPanel
                description={
                  !stockFilterActive
                    ? "No repeat out-of-stock items in this period"
                    : "No repeat out-of-stock items for selected filters"
                }
              />
            )}
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card
            style={panelStyle}
            title={
              <PanelTitle
                icon={<InboxOutlined />}
                title="Unvisited clients"
                subtitle="Known clients without a visit in the selected period"
              />
            }
            extra={
              <Space size={6}>
                <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                  {formatNumber(
                    unvisitedClients?.count ??
                      unvisitedRows.length,
                  )}
                </Tag>
                {comparisonLoading ? <DeltaBadge status="loading" /> : null}
                {comparisonView ? (
                  <DeltaBadge
                    unit="count"
                    delta={countDelta(
                      unvisitedClients?.count ?? unvisitedRows.length,
                      comparisonDashboard?.unvisitedClients?.count ??
                        comparisonUnvisitedIds.size,
                    )}
                    positiveIsGood={false}
                    from={formatNumber(
                      comparisonDashboard?.unvisitedClients?.count ??
                        comparisonUnvisitedIds.size,
                    )}
                    to={formatNumber(unvisitedClients?.count ?? unvisitedRows.length)}
                    fromLabel={comparisonLabel}
                    toLabel={selectedLabel}
                    note={trans("Unvisited clients")}
                  />
                ) : null}
              </Space>
            }
            bodyStyle={{ padding: 16 }}
          >
            {unvisitedRows.length ? (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Input
                  allowClear
                  value={unvisitedSearch}
                  prefix={<SearchOutlined style={{ color: COLORS.neutral }} />}
                  suffix={
                    normalizedUnvisitedSearch ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatNumber(filteredUnvisitedClients.length)} /{" "}
                        {formatNumber(unvisitedRows.length)}
                      </Text>
                    ) : null
                  }
                  placeholder={trans("Search unvisited clients")}
                  aria-label={trans("Search unvisited clients")}
                  onChange={(event) => setUnvisitedSearch(event.target.value)}
                  style={{ maxWidth: 380 }}
                />
                <Table
                  key={unvisitedSearch}
                  size="small"
                  rowKey={(row, index) =>
                    `${row?.clientId || row?.clientName || "client"}-${index}`
                  }
                  dataSource={filteredUnvisitedClients}
                  locale={{
                    emptyText: <EmptyPanel description="No clients match your search" />,
                  }}
                  pagination={
                    filteredUnvisitedClients.length > 10
                      ? {
                          defaultPageSize: 10,
                          size: "small",
                          showSizeChanger: true,
                          pageSizeOptions: ["10", "25", "50"],
                          showTotal: (total, range) =>
                            `${formatNumber(range[0])}–${formatNumber(
                              range[1],
                            )} / ${formatNumber(total)} ${trans("clients")}`,
                        }
                      : false
                  }
                  scroll={{ x: comparisonView || comparisonLoading ? 800 : 680 }}
                  columns={[
                    {
                      title: trans("Client"),
                      dataIndex: "clientName",
                      key: "clientName",
                      render: (value, row) => (
                        <Text strong>{displayText(value || row?.clientId)}</Text>
                      ),
                    },
                    {
                      title: trans("Region / City"),
                      key: "location",
                      render: (_, row) => locationText(row?.region, row?.city),
                    },
                    {
                      title: trans("Channel"),
                      dataIndex: "type",
                      key: "type",
                      render: (value) => formatChannel(value),
                    },
                    ...(comparisonView || comparisonLoading
                      ? [
                          {
                            title: comparisonLabel,
                            key: "comparisonVisit",
                            render: (_, row) => {
                              if (!comparisonView) {
                                return <DeltaBadge status="loading" />;
                              }
                              const clientId = Number(row?.clientId);
                              if (comparisonUnvisitedIds.has(clientId)) {
                                return (
                                  <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                                    {trans("Also unvisited")}
                                  </Tag>
                                );
                              }
                              const previous = previousVisits(clientId);
                              return previous > 0 ? (
                                <Tag color="green" style={{ marginInlineEnd: 0 }}>
                                  {trans("Visited")} ({formatNumber(previous)})
                                </Tag>
                              ) : (
                                <Text type="secondary">—</Text>
                              );
                            },
                          },
                        ]
                      : []),
                    {
                      title: trans("Last visit"),
                      dataIndex: "lastVisitAt",
                      key: "lastVisitAt",
                      render: (value) => formatDate(value),
                    },
                  ]}
                />
              </Space>
            ) : (
              <EmptyPanel description="All known clients were visited in this period" />
            )}
          </Card>
        </Col>
      </Row>
    </section>
    </ComparisonLabelsContext.Provider>
  );
}
